/**
 * Phase 7 — Admin Panel — PRD §5.9
 *
 * Admin-only screen for user management.
 * Tab 1: User Management — list, add, change role, reset password, delete.
 * Tab 2: Workstream Management.
 * Tab 3: Watch Areas — the taxonomy pass 2 files every new signal into.
 * Tab 4: App Settings — morning-brief time, one API-key section per LLM
 *        provider, feed coverage, and the junk-signal purge.
 * Tab 5: Analytics.
 *
 * Access: admin role only. Non-admins are redirected to /dashboard.
 */

import { api, ApiError } from "../lib/api";
import { ensureAppTimezone, fmtDate, fmtDateTime, fmtSchedule } from "../lib/datetime";
import { invalidateLLMSettings } from "../lib/llm";
import { navigate, registerPageCleanup } from "../lib/router";
import { renderHeader } from "../components/header";

// ── Types ──────────────────────────────────────────────────────────────────

interface UserRecord {
  id: string;
  username: string;
  role: string;
  created_at: string;
  last_login: string | null;
  telegram_user_id: string | null;
}

interface WorkstreamRecord {
  id: string;
  name: string;
  signalCount: number;
  definition: string | null;
}

/** One watch area — see backend/routers/watch_areas.py. */
interface WatchAreaRecord {
  slug: string;
  label: string;
  definition: string;
  active: boolean;
  origin: string;
  signalCount: number;
  createdAt: string;
  updatedAt: string;
}

/**
 * The areas plus the two piles that are not areas. `unassigned` is what the
 * model was asked about and could not place — the number that says whether the
 * taxonomy has a hole in it. `unrouted` was never asked at all.
 */
interface WatchAreaList {
  areas: WatchAreaRecord[];
  unassigned: number;
  unrouted: number;
}

/** One change the reviewer proposes — see backend/routers/watch_areas.py. */
interface ProposalRecord {
  id: string;
  kind: "add" | "merge";
  slug: string;
  label: string | null;
  definition: string;
  mergeInto: string | null;
  rationale: string;
  evidence: { id: string; title: string }[];
  status: "pending" | "accepted" | "rejected";
  createdAt: string;
  decidedAt: string | null;
}

/** What one run of the reviewer produced, including what it could not use. */
interface ReviewResponse {
  proposals: ProposalRecord[];
  dropped: string[];
  pileRead: number;
  pileTotal: number;
  message?: string;
}

/** One provider's key state — see backend/routers/admin_settings.py. */
interface LLMKeyStatus {
  provider: string;
  providerLabel: string;
  keyHint: string;
  keyUrl: string;
  envVar: string;
  free: boolean;
  source: "database" | "env" | "none";
  configured: boolean;
  masked: string | null;
  stubMode: boolean;
  decryptFailed: boolean;
  envFallbackAvailable: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
  lastValidatedAt: string | null;
  lastValidationStatus: string | null;
  lastValidationMessage: string | null;
}

/** What paces one provider, and where that number came from. */
interface ProviderRateLimit {
  provider: string;
  providerLabel: string;
  rpm: number;
  stored: number | null;
  envVar: string;
  envRpm: number | null;
  defaultRpm: number;
  /** Tokens per minute — usually the limit that actually binds on a free
   *  tier, where requests can sit well under their ceiling while tokens run
   *  at twice theirs. */
  tpm: number;
  storedTpm: number | null;
  envVarTpm: string;
  envTpm: number | null;
  defaultTpm: number;
}

/** Dashboard feeds the collector never visits — see backend/feed_drift.py. */
interface FeedCoverageReport {
  collectorFeedCount: number;
  dashboardFeedCount: number;
  missing: { name: string; url: string; panel: string }[];
}

/** Whether each configured feed actually answered — see backend/models/feed_health.py. */
interface FeedHealthRow {
  name: string;
  url: string;
  /** "ok" | "stale" | "fail" | "unknown" — unknown means never checked. */
  status: string;
  detail: string | null;
  lastCheckedAt: string | null;
  lastOkAt: string | null;
  consecutiveFailures: number;
  lastError: string | null;
  lastSource: string | null;
  inConfig: boolean;
}

interface FeedHealthReport {
  feeds: FeedHealthRow[];
  okCount: number;
  staleCount: number;
  failCount: number;
  unknownCount: number;
  checking: boolean;
  lastCheckedAt: string | null;
}

/** Placeholder signals stored without a real analysis pass — see backend/routers/admin_maintenance.py. */
interface StubSignalReport {
  count: number;
  totalSignals: number;
  annotatedCount: number;
  previewLimit: number;
  byDate: { date: string; count: number }[];
  preview: {
    id: string;
    title: string;
    source: string;
    dateAdded: string;
    annotated: boolean;
  }[];
}

/** The v6 backfill's progress, as `GET /admin/settings/backfill` reports it.
 *
 * `total` and `processed` come from the checkpoint row and survive a restart;
 * `lines` and `etaSeconds` come from the process currently running it and do
 * not. A job resumed after a deploy therefore shows full progress and an
 * unknown ETA until it has finished a batch, which is the honest answer — this
 * process has not yet watched it work. */
interface BackfillProgress {
  status: string;
  processed: number;
  skipped: number;
  total: number | null;
  remaining: number | null;
  corpus?: number | null;
  /** How much of the corpus carries a classification at all — from whatever
   * wrote it, and it only goes up. `processed` is this job's own tally and is
   * reset when a finished job is started again, so the headline uses this. */
  classified: number;
  reason: string | null;
  startedAt: string | null;
  updatedAt: string | null;
  runningHere: boolean;
  resumable: boolean;
  lines: string[];
  etaSeconds: number | null;
  eta: string;
  window: { allowed: boolean; reason: string } | null;
  budget: {
    bucket: string;
    used: number;
    cap: number;
    ceiling: number;
    remaining: number;
    quotaDay: string;
    resetTz: string;
    allowed: boolean;
    reason: string;
  } | null;
  types: { label: string; count: number; share: number }[];
  claimStatuses: { label: string; count: number; share: number }[];
}

interface BackfillDryRun {
  sampled: number;
  classified: number;
  rows: {
    id: string;
    ref: string | null;
    title: string;
    dateAdded: string;
    signalType: string | null;
    claimStatus: string | null;
    claimTrigger: string | null;
    eventDate: string | null;
    horizonBefore: string | null;
    horizonAfter: string | null;
    rationale: string | null;
    story: string;
    candidates: number;
  }[];
  notes: string[];
  error: string | null;
  ranAt: string;
}

interface BackfillSampleRow {
  id: string;
  ref: string | null;
  title: string;
  dateAdded: string;
  signalType: string | null;
  claimStatus: string | null;
  eventDate: string | null;
  horizon: string | null;
  horizonLegacy: string | null;
  rationale: string | null;
  novelty: number | null;
  storyId: string | null;
}

// ── Module state ───────────────────────────────────────────────────────────

let _users: UserRecord[] = [];
let _workstreams: WorkstreamRecord[] = [];
let _wsFilter = "";
let _watchAreas: WatchAreaList = { areas: [], unassigned: 0, unrouted: 0 };
let _waFilter = "";
let _proposals: ProposalRecord[] = [];
let _reviewNotice = "";
let _reviewFailed = false;
let _activeTab: "users" | "workstreams" | "watchareas" | "settings" | "analytics" = "users";
let _keyStatuses: LLMKeyStatus[] = [];
let _stubReport: StubSignalReport | null = null;
let _stubNotice = "";
let _feedCoverage: FeedCoverageReport | null = null;
let _feedCoverageNotice = "";
let _feedHealth: FeedHealthReport | null = null;
let _feedHealthNotice = "";
// Poll handle for a running check. Cleared when the check finishes or the tab
// is left — a timer that outlives its page is how a dead tab keeps hitting the
// API forever.
let _feedHealthPoll: number | null = null;
let _rateLimits: ProviderRateLimit[] = [];
let _workWindows: WorkWindows | null = null;
let _dailyBudget: DailyBudget | null = null;
let _backfill: BackfillProgress | null = null;
let _backfillNotice = "";
let _backfillDryRun: BackfillDryRun | null = null;
let _backfillSample: BackfillSampleRow[] | null = null;
// Poll handle, cleared the moment the job is no longer running here or the tab
// is left. A timer that outlives its page is how a dead tab keeps hitting the
// API forever — the same trap `_feedHealthPoll` exists to avoid.
let _backfillPoll: number | null = null;

// ── Entry point ────────────────────────────────────────────────────────────

export async function renderAdminPage(root: HTMLElement): Promise<void> {
  // Every tab here renders timestamps; the zone has to be in hand first.
  await ensureAppTimezone();
  _activeTab = "users";
  // A 2s interval that survives navigation keeps polling a dead page forever.
  registerPageCleanup(stopFeedHealthPolling);
  registerPageCleanup(stopBackfillPoll);
  root.appendChild(renderHeader());

  const shell = document.createElement("div");
  shell.className = "ap-page";
  shell.innerHTML = `
    <div class="ap-container">
      <div class="ap-header-row">
        <h1 class="ap-title">Admin Panel</h1>
      </div>
      <div class="ap-tabs">
        <button class="ap-tab active" data-tab="users">User Management</button>
        <button class="ap-tab" data-tab="workstreams">Workstream Management</button>
        <button class="ap-tab" data-tab="watchareas">Watch Areas</button>
        <button class="ap-tab" data-tab="settings">App Settings</button>
        <button class="ap-tab" data-tab="analytics">Analytics</button>
      </div>
      <div class="ap-content" id="ap-content"></div>
    </div>
  `;
  root.appendChild(shell);

  // Wire tab buttons
  const tabBtns = shell.querySelectorAll<HTMLButtonElement>(".ap-tab");
  tabBtns.forEach((btn) => {
    btn.addEventListener("click", () => {
      tabBtns.forEach((b) => b.classList.remove("active"));
      btn.classList.add("active");
      _activeTab = btn.dataset["tab"] as
        | "users"
        | "workstreams"
        | "watchareas"
        | "settings"
        | "analytics";
      void renderTabContent();
    });
  });

  await loadUsers();
  void renderTabContent();
}

// ── Data loading ───────────────────────────────────────────────────────────

async function loadUsers(): Promise<void> {
  try {
    _users = await api<UserRecord[]>("/users");
  } catch (err) {
    _users = [];
    if (err instanceof ApiError && err.status === 403) {
      navigate("/dashboard");
    }
  }
}

async function loadWorkstreams(): Promise<void> {
  try {
    _workstreams = await api<WorkstreamRecord[]>("/workstreams");
  } catch {
    _workstreams = [];
  }
}

async function loadWatchAreas(): Promise<void> {
  try {
    _watchAreas = await api<WatchAreaList>("/watch-areas");
  } catch {
    _watchAreas = { areas: [], unassigned: 0, unrouted: 0 };
  }
}

async function loadProposals(): Promise<void> {
  try {
    _proposals = await api<ProposalRecord[]>("/watch-areas/proposals");
  } catch {
    _proposals = [];
  }
}

async function loadKeyStatus(): Promise<void> {
  try {
    _keyStatuses = await api<LLMKeyStatus[]>("/admin/settings/llm-keys");
  } catch {
    _keyStatuses = [];
  }
}

interface WindowSpan {
  name: string;
  start: string;
  end: string;
  minutes: number;
}

interface DayPlan {
  date: string;
  label: string;
  windows: WindowSpan[];
  /** An inverted schedule, a window too short to be worth having, an overlap.
   *  Warnings rather than errors: every one describes a setting somebody chose,
   *  and the panel's job is to report the consequence, not to overrule it. */
  warnings: string[];
}

interface WorkWindows {
  days: DayPlan[];
  timezone: string;
  minWindowMinutes: number;
}

interface ModelDay {
  bucket: string;
  label: string;
  used: number;
  /** 0 means uncapped, which is the shipped state — not "no requests left". */
  cap: number;
  reservedCeiling: number;
}

interface DailyBudget {
  /** **Not the app zone.** A provider's allowance resets on the provider's
   *  clock, so on an Asia/Singapore install this day rolls over mid-afternoon.
   *  The panel has to name the zone or the numbers look like they reset at
   *  random times. */
  resetTz: string;
  quotaDay: string;
  reservePercent: number;
  reservedRoles: string[];
  secondsUntilReset: number;
  models: ModelDay[];
}

async function loadWorkWindows(): Promise<void> {
  try {
    _workWindows = await api<WorkWindows>("/admin/settings/work-windows");
  } catch {
    _workWindows = null;
  }
}

async function loadDailyBudget(): Promise<void> {
  try {
    _dailyBudget = await api<DailyBudget>("/admin/settings/daily-budget");
  } catch {
    _dailyBudget = null;
  }
}

async function loadBackfill(): Promise<void> {
  try {
    _backfill = await api<BackfillProgress>("/admin/settings/backfill");
  } catch {
    _backfill = null;
  }
}

async function loadRateLimits(): Promise<void> {
  try {
    _rateLimits = await api<ProviderRateLimit[]>("/admin/settings/rate-limits");
  } catch {
    _rateLimits = [];
  }
}

async function loadFeedCoverage(): Promise<void> {
  try {
    _feedCoverage = await api<FeedCoverageReport>("/admin/settings/feed-coverage");
  } catch {
    _feedCoverage = null;
  }
}

async function loadFeedHealth(): Promise<void> {
  try {
    _feedHealth = await api<FeedHealthReport>("/admin/settings/feed-health");
  } catch {
    _feedHealth = null;
  }
}

async function loadStubReport(): Promise<void> {
  try {
    _stubReport = await api<StubSignalReport>("/admin/maintenance/stub-signals");
  } catch {
    _stubReport = null;
  }
}

// ── Tab rendering ──────────────────────────────────────────────────────────

async function renderTabContent(): Promise<void> {
  const content = document.getElementById("ap-content");
  if (!content) return;
  if (_activeTab === "users") {
    renderUsersTab(content);
  } else if (_activeTab === "workstreams") {
    _wsFilter = "";
    await loadWorkstreams();
    renderWorkstreamsTab(content);
  } else if (_activeTab === "watchareas") {
    _waFilter = "";
    _reviewNotice = "";
    _reviewFailed = false;
    await Promise.all([loadWatchAreas(), loadProposals()]);
    renderWatchAreasTab(content);
  } else if (_activeTab === "analytics") {
    await renderAnalyticsTab(content);
  } else {
    await renderSettingsTab(content);
  }
}

/** The brief schedule, and the scan schedule it follows. */
interface BriefSettings {
  briefTime: string;
  briefEnabled: boolean;
  timezone: string;
  days: string[];
  scanEnabled: boolean;
  scanTime: string;
  nextBriefAt: string | null;
  briefChannelChatId: string;
  weeklyEnabled: boolean;
  weeklyDay: string;
  weeklyTime: string;
  nextWeeklyBriefAt: string | null;
  weeklyHoldAsDraft: boolean;
  weeklyFallbackTime: string;
  /** Server-computed: the fallback would fire before the weekly it stands in
   *  for, so the scheduler refuses to run it. */
  weeklyFallbackBeforeWeekly: boolean;
}

const DAY_LABELS: Record<string, string> = {
  mon: "Mon", tue: "Tue", wed: "Wed", thu: "Thu", fri: "Fri", sat: "Sat", sun: "Sun",
};

async function renderSettingsTab(container: HTMLElement): Promise<void> {
  container.innerHTML = `<p class="muted">Loading…</p>`;

  let briefSettings: BriefSettings | null = null;
  try {
    [briefSettings] = await Promise.all([
      api<BriefSettings>("/collect/settings"),
      ensureAppTimezone(),
    ]);
  } catch {
    briefSettings = null;
  }
  await loadKeyStatus();
  await loadRateLimits();
  await loadWorkWindows();
  await loadDailyBudget();
  await loadBackfill();
  await loadFeedCoverage();
  await loadFeedHealth();
  await loadStubReport();

  container.innerHTML = "";

  const briefSection = document.createElement("div");
  briefSection.className = "ap-settings-section";
  if (briefSettings) {
    briefSection.innerHTML = `
      <h3>Morning Brief</h3>
      <div class="field-group">
        <label for="ap-brief-time">Send time
          <span class="muted">(${esc(briefSettings.timezone)} — the app time zone)</span>
        </label>
        <input type="time" id="ap-brief-time" value="${esc(briefSettings.briefTime)}" />
      </div>
      <label class="ap-brief-toggle">
        <input type="checkbox" id="ap-brief-enabled" ${briefSettings.briefEnabled ? "checked" : ""} />
        <span>Send the morning brief</span>
      </label>
      ${briefScheduleNote(briefSettings)}

      <div class="field-group">
        <label for="ap-brief-channel">Telegram channel <span class="muted">(optional)</span></label>
        <input type="text" id="ap-brief-channel" placeholder="@kassandra or -1001234567890"
               value="${esc(briefSettings.briefChannelChatId)}" />
      </div>
      <p class="muted">
        Set this and every brief goes to that channel instead of to each linked
        user — one canonical copy. Leave it blank to keep sending direct messages.
        The bot must be an administrator of the channel.
      </p>

      <h3>Weekly Brief</h3>
      <label class="ap-brief-toggle">
        <input type="checkbox" id="ap-weekly-enabled" ${briefSettings.weeklyEnabled ? "checked" : ""} />
        <span>Send the weekly deep brief</span>
      </label>
      <div class="field-group">
        <label for="ap-weekly-day">Day</label>
        <select id="ap-weekly-day">
          ${Object.entries(DAY_LABELS).map(([v, l]) =>
            `<option value="${v}"${briefSettings!.weeklyDay === v ? " selected" : ""}>${l}</option>`
          ).join("")}
        </select>
      </div>
      <div class="field-group">
        <label for="ap-weekly-time">Send time
          <span class="muted">(${esc(briefSettings.timezone)})</span>
        </label>
        <input type="time" id="ap-weekly-time" value="${esc(briefSettings.weeklyTime)}" />
      </div>
      ${briefSettings.weeklyTime !== "12:00" ? `
      <p class="muted ap-weekly-default-note">Kassandra's default weekly time is
        <strong>12:00</strong>; this install is set to
        <strong>${esc(briefSettings.weeklyTime)}</strong>. That is kept, not overwritten —
        change it above if you want the default.</p>` : ""}
      <label class="ap-brief-toggle">
        <input type="checkbox" id="ap-weekly-hold"
          ${briefSettings.weeklyHoldAsDraft ? "checked" : ""} />
        <span>Hold weekly as draft</span>
      </label>
      <p class="muted">With this on, the weekly is written but not sent. It waits in the
        dashboard with a <strong>Send now</strong> button. With it off — the default — the
        weekly sends on its schedule exactly like the daily.</p>
      <div class="field-group">
        <label for="ap-weekly-fallback">Fallback digest time
          <span class="muted">(${esc(briefSettings.timezone)})</span>
        </label>
        <input type="time" id="ap-weekly-fallback"
          value="${esc(briefSettings.weeklyFallbackTime)}" />
      </div>
      <p class="muted">If no weekly could be written at all by this time, the week's signals
        go out as a plain list so the slot is not missed. A weekly that is merely late is
        not this case — the delivery sweep sends the real one when it appears.</p>
      ${briefSettings.weeklyFallbackBeforeWeekly ? `
      <p class="ap-warning-text">The fallback time is not after the weekly's own send time, so it
        would fire before there was anything to wait for. <strong>It is switched off</strong>
        until you set a later time.</p>` : ""}
      <p class="muted">
        The weekly covers the whole local calendar week, Monday to Sunday. Its day
        is its own — unlike the morning brief, it does not follow the scan's days.
      </p>

      <p class="muted ap-settings-err" id="ap-settings-err"></p>
      <div class="ap-settings-actions">
        <button class="primary" id="ap-settings-save">Save</button>
        <span class="muted" id="ap-settings-saved" style="display:none">Saved.</span>
      </div>
    `;
  } else {
    briefSection.innerHTML = `<h3>Morning Brief</h3><p class="muted">Could not load settings.</p>`;
  }
  container.appendChild(briefSection);

  if (briefSettings) {
    briefSection.querySelector("#ap-settings-save")!.addEventListener("click", async () => {
      const timeVal = briefSection.querySelector<HTMLInputElement>("#ap-brief-time")!.value;
      const enabled = briefSection.querySelector<HTMLInputElement>("#ap-brief-enabled")!.checked;
      const channel = briefSection.querySelector<HTMLInputElement>("#ap-brief-channel")!.value;
      const weeklyEnabled = briefSection.querySelector<HTMLInputElement>("#ap-weekly-enabled")!.checked;
      const weeklyDay = briefSection.querySelector<HTMLSelectElement>("#ap-weekly-day")!.value;
      const weeklyTime = briefSection.querySelector<HTMLInputElement>("#ap-weekly-time")!.value;
      const weeklyHoldAsDraft = briefSection.querySelector<HTMLInputElement>("#ap-weekly-hold")!.checked;
      const weeklyFallbackTime = briefSection.querySelector<HTMLInputElement>("#ap-weekly-fallback")!.value;
      const errEl = briefSection.querySelector<HTMLElement>("#ap-settings-err")!;
      const savedEl = briefSection.querySelector<HTMLElement>("#ap-settings-saved")!;
      errEl.textContent = "";
      savedEl.style.display = "none";
      try {
        const updated = await api<BriefSettings>("/collect/settings", {
          method: "POST",
          body: {
            briefTime: timeVal,
            briefEnabled: enabled,
            briefChannelChatId: channel,
            weeklyEnabled,
            weeklyDay,
            weeklyTime,
            weeklyHoldAsDraft,
            weeklyFallbackTime,
          },
        });
        // Re-render the note: saving can change what it says — a brief switched
        // off, or a time that now precedes the scan.
        const note = briefSection.querySelector<HTMLElement>("#ap-brief-note");
        if (note) note.outerHTML = briefScheduleNote(updated);
        savedEl.style.display = "";
      } catch (err) {
        errEl.textContent = err instanceof ApiError ? err.message : "Save failed.";
      }
    });
  }

  if (_keyStatuses.length === 0) {
    const failed = document.createElement("div");
    failed.className = "ap-settings-section";
    failed.innerHTML = `<h3>LLM API Keys</h3><p class="muted">Could not load key status.</p>`;
    container.appendChild(failed);
  } else {
    for (const status of _keyStatuses) container.appendChild(renderKeySection(status));
  }
  container.appendChild(renderRateLimitSection());
  container.appendChild(renderWorkWindowsSection());
  container.appendChild(renderDailyBudgetSection());
  container.appendChild(renderBackfillSection());
  container.appendChild(renderFeedCoverageSection());
  container.appendChild(renderFeedHealthSection());
  container.appendChild(renderJunkSection());
}

/** What this schedule actually does, in the cases where the time alone misleads.
 *
 * The brief follows the scan: same days, same zone. So "09:00" is not the whole
 * answer — on a schedule with no scan there is no brief at all, and a brief
 * before the scan reports yesterday. Each of those used to be discoverable only
 * by waiting a day and noticing nothing arrived. */
function briefScheduleNote(s: BriefSettings): string {
  const days = s.days.map((d) => DAY_LABELS[d] ?? d).join(", ");
  const parts: string[] = [];

  if (!s.scanEnabled || s.days.length === 0) {
    parts.push(
      `<strong>No scheduled scan, so the morning brief will not be sent.</strong>
       Turn on automated scanning in Signal Management → Automated Scanning.`
    );
  } else if (!s.briefEnabled) {
    parts.push(`The brief is switched off. The scan still runs on ${esc(days)}.`);
  } else {
    parts.push(`Sent on ${esc(days)}, the days the scan runs, in ${esc(s.timezone)}.`);
    if (s.briefTime <= s.scanTime) {
      parts.push(
        `This time is before the scan at ${esc(s.scanTime)}, so each brief covers
         the previous day's signals.`
      );
    }
    if (s.nextBriefAt) parts.push(`Next: ${esc(fmtSchedule(s.nextBriefAt))}.`);
    parts.push(`A day with no signals gets no brief.`);
  }

  return `<p class="muted ap-brief-note" id="ap-brief-note">${parts.join(" ")}</p>`;
}

async function refreshSettingsTab(): Promise<void> {
  // A key change flips whether a provider counts as configured, which the
  // header's model chip reports — drop its cache so the chip doesn't keep
  // showing a warning for a key that was just entered.
  invalidateLLMSettings();
  const content = document.getElementById("ap-content");
  if (!content || _activeTab !== "settings") return;
  await renderSettingsTab(content);
}

// ── LLM provider API key sections ─────────────────────────────────────────
//
// One section per provider, rendered from the server's list rather than
// hardcoded, so adding a provider in backend/llm_providers.py needs no change
// here. Element ids are namespaced by provider — two sections are on the page
// at once and duplicate ids would wire the wrong buttons.

function renderKeySection(s: LLMKeyStatus): HTMLElement {
  const section = document.createElement("div");
  section.className = "ap-settings-section ap-key-section";

  const p = s.provider;

  let badgeCls = "ap-status-ok";
  let badgeText = "Active";
  if (s.decryptFailed) {
    badgeCls = "ap-status-error";
    badgeText = "Stored key unreadable — SECRET_KEY changed";
  } else if (s.stubMode) {
    badgeCls = "ap-status-warn";
    badgeText = "Not configured — models from this provider can't run";
  } else if (s.lastValidationStatus === "auth_failed") {
    badgeCls = "ap-status-error";
    badgeText = "Last check failed";
  } else if (!s.configured) {
    badgeCls = "ap-status-warn";
    badgeText = "Not configured";
  }

  const sourceLabel =
    s.source === "database" ? "Stored in app" : s.source === "env" ? "Environment variable" : "Not configured";
  const keyDisplay = s.masked
    ? `<code class="ap-key-mono">${esc(s.masked)}</code>`
    : `<span class="muted">Not configured</span>`;
  const lastChanged = s.updatedAt
    ? `${fmtDateTime(s.updatedAt)}${s.updatedBy ? ` by ${esc(s.updatedBy)}` : ""}`
    : "—";
  const lastChecked = s.lastValidatedAt
    ? `${fmtDateTime(s.lastValidatedAt)}${s.lastValidationMessage ? ` — ${esc(s.lastValidationMessage)}` : ""}`
    : "—";

  section.innerHTML = `
    <h3>${esc(s.providerLabel)} API Key${s.free ? ' <span class="ap-free-tag">free</span>' : ""}</h3>
    <p class="muted ap-key-hint">
      ${esc(s.keyHint)} Used by whichever analysis roles are pointed at this provider.
      Stored encrypted — the <code>${esc(s.envVar)}</code> environment variable remains
      the fallback. <a href="${esc(s.keyUrl)}" target="_blank" rel="noopener noreferrer">Get a key</a>.
    </p>
    <span class="ap-status-badge ${badgeCls}">${esc(badgeText)}</span>
    <div class="ap-key-rows">
      <div class="ap-key-row"><span class="ap-key-row-label">Key</span><span class="ap-key-row-value">${keyDisplay}</span></div>
      <div class="ap-key-row"><span class="ap-key-row-label">Source</span><span class="ap-key-row-value">${esc(sourceLabel)}</span></div>
      <div class="ap-key-row"><span class="ap-key-row-label">Last changed</span><span class="ap-key-row-value">${lastChanged}</span></div>
      <div class="ap-key-row"><span class="ap-key-row-label">Last checked</span><span class="ap-key-row-value">${lastChecked}</span></div>
    </div>
    <div class="ap-settings-actions">
      <button class="primary" id="ak-replace-${esc(p)}">+ Replace Key</button>
      <button class="ap-action-btn" id="ak-test-${esc(p)}">Test Key</button>
      ${s.source === "database" ? `<button class="ap-action-btn ap-action-delete" id="ak-clear-${esc(p)}">Use environment variable</button>` : ""}
    </div>
  `;

  section.querySelector(`#ak-replace-${CSS.escape(p)}`)!.addEventListener("click", () => openReplaceKeyModal(s));

  section.querySelector(`#ak-test-${CSS.escape(p)}`)!.addEventListener("click", async (e) => {
    const btn = e.currentTarget as HTMLButtonElement;
    const originalText = btn.textContent;
    btn.disabled = true;
    btn.textContent = "Testing…";
    try {
      await api(`/admin/settings/llm-keys/${encodeURIComponent(p)}/test`, { method: "POST" });
    } catch {
      // Result (including failure) is recorded server-side and shown after refresh.
    } finally {
      btn.disabled = false;
      btn.textContent = originalText;
      await refreshSettingsTab();
    }
  });

  section.querySelector(`#ak-clear-${CSS.escape(p)}`)?.addEventListener("click", () => openClearKeyModal(s));

  return section;
}

function openReplaceKeyModal(s: LLMKeyStatus): void {
  const overlay = createOverlay();
  overlay.innerHTML = `
    <div class="ap-modal" role="dialog" aria-modal="true" aria-label="Replace ${esc(s.providerLabel)} API Key">
      <div class="ap-modal-header">
        <h2>Replace ${esc(s.providerLabel)} Key</h2>
        <button class="ap-modal-close" aria-label="Close">✕</button>
      </div>
      <div class="ap-modal-body">
        <p class="muted" style="font-size:13px;margin:0 0 var(--space-sm)">
          The key is validated with a minimal live call before it's saved, and is never
          shown again once stored.
        </p>
        <label class="ap-field-label" for="ak-input">API Key</label>
        <input
          type="password"
          id="ak-input"
          class="ap-input"
          placeholder="${s.provider === "google" ? "AQ.Ab… or AIza…" : "sk-ant-…"}"
          autocomplete="off"
          spellcheck="false"
        />
        <label class="ap-key-skip-label" id="ak-skip-label" style="display:none">
          <input type="checkbox" id="ak-skip" /> Save without validating
        </label>
        <p class="ap-error" id="ak-error" style="display:none;"></p>
      </div>
      <div class="ap-modal-footer">
        <button class="ap-modal-cancel">Cancel</button>
        <button class="primary" id="ak-save">Save</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  const input = overlay.querySelector<HTMLInputElement>("#ak-input")!;
  const skipLabel = overlay.querySelector<HTMLElement>("#ak-skip-label")!;
  const skipBox = overlay.querySelector<HTMLInputElement>("#ak-skip")!;
  const errorEl = overlay.querySelector<HTMLParagraphElement>("#ak-error")!;
  const saveBtn = overlay.querySelector<HTMLButtonElement>("#ak-save")!;
  input.focus();

  const close = () => overlay.remove();
  wireClose(overlay, close);

  saveBtn.addEventListener("click", async () => {
    errorEl.style.display = "none";
    const apiKey = input.value.trim();
    if (!apiKey) { showErr(errorEl, "Enter an API key."); return; }

    saveBtn.disabled = true;
    saveBtn.textContent = "Validating…";
    try {
      await api(`/admin/settings/llm-keys/${encodeURIComponent(s.provider)}`, {
        method: "PUT",
        body: { apiKey, skipValidation: skipBox.checked },
      });
      input.value = "";
      close();
      await refreshSettingsTab();
    } catch (err) {
      // Offer the override on a rejection as well as a timeout. A validator
      // can be wrong about a key that works — Google moved AI Studio from
      // AIza to AQ.Ab keys mid-flight — and an operator holding a key they
      // know is good shouldn't be locked out by our opinion of it. A stored
      // bad key is recoverable: it aborts the next run with a permanent
      // error rather than writing placeholder signals.
      if (err instanceof ApiError && (err.status === 504 || err.status === 400)) {
        skipLabel.style.display = "";
        showErr(
          errorEl,
          `${err.message} If you're sure this key is correct, tick "Save without validating".`
        );
      } else {
        showErr(errorEl, err instanceof ApiError ? err.message : "Failed to save key.");
      }
    } finally {
      saveBtn.disabled = false;
      saveBtn.textContent = "Save";
    }
  });
}

function openClearKeyModal(s: LLMKeyStatus): void {
  const overlay = createOverlay();
  overlay.innerHTML = `
    <div class="ap-modal" role="dialog" aria-modal="true">
      <div class="ap-modal-header">
        <h2>Use Environment Variable</h2>
        <button class="ap-modal-close" aria-label="Close">✕</button>
      </div>
      <div class="ap-modal-body">
        <p class="ap-warning-text">
          This clears the stored ${esc(s.providerLabel)} key and reverts to the
          <code>${esc(s.envVar)}</code> environment variable.
          ${
            s.envFallbackAvailable
              ? "That variable is set, so this provider keeps working."
              : "That variable is <strong>not</strong> set, so any analysis role pointed at this provider will stop running."
          }
        </p>
        <p class="ap-error" id="ak-clear-error" style="display:none;"></p>
      </div>
      <div class="ap-modal-footer">
        <button class="ap-modal-cancel">Cancel</button>
        <button class="destructive" id="ak-clear-confirm">Use Environment Variable</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const close = () => overlay.remove();
  wireClose(overlay, close);

  overlay.querySelector("#ak-clear-confirm")!.addEventListener("click", async () => {
    const errorEl = overlay.querySelector("#ak-clear-error") as HTMLParagraphElement;
    try {
      await api(`/admin/settings/llm-keys/${encodeURIComponent(s.provider)}`, { method: "DELETE" });
      close();
      await refreshSettingsTab();
    } catch (err) {
      showErr(errorEl, err instanceof ApiError ? err.message : "Failed to clear key.");
    }
  });
}

// ── Provider rate limits ──────────────────────────────────────────────────
//
// The number the rate-limit modal points at. Google publishes the real figure
// per key in AI Studio rather than in its docs, so the shipped default is a
// cautious guess that is wrong for every key — and until now the only way to
// change it was an environment variable and a redeploy.

/** Where the number in the box came from, said plainly — otherwise saving one
 *  silently overrides a deploy-time setting nobody remembers making. */
function rateLimitSource(r: ProviderRateLimit): string {
  if (r.stored !== null) return "Set here";
  if (r.envRpm !== null) return `From ${r.envVar}`;
  return `Default (${r.defaultRpm})`;
}

function tpmSource(r: ProviderRateLimit): string {
  if (r.storedTpm !== null) return "Set here";
  if (r.envTpm !== null) return `From ${r.envVarTpm}`;
  return `Default (${r.defaultTpm.toLocaleString()})`;
}

/** What each part of the day is for, derived from the schedules already set.
 *
 * **Read-only, and that is the design.** These intervals come from the scan,
 * daily and weekly times, which are edited in the panel above. A control here
 * would be a second schedule, and the two would disagree the first time
 * somebody moved a brief.
 *
 * Two days: today, and the next weekly day. The weekly's shape is the one that
 * decides whether a weekly arrives, and it is the one nobody sees until it has
 * already gone wrong. */
function renderWorkWindowsSection(): HTMLDivElement {
  const section = document.createElement("div");
  section.className = "ap-settings-section";

  if (!_workWindows) {
    section.innerHTML = `<h3>Work windows</h3>
      <p class="muted">Could not derive the day's windows.</p>`;
    return section;
  }

  const w = _workWindows;
  const days = w.days
    .map((day) => {
      const rows = day.windows
        .map((win) => {
          // The backfill's leftovers are the shape of the day rather than
          // scheduled work, so they read quieter than the windows a job owns.
          const cls = win.name === "backfill_allowed" ? "aw-row aw-spare" : "aw-row";
          const short =
            win.name !== "backfill_allowed" && win.minutes < w.minWindowMinutes;
          return `<tr class="${cls}">
            <td class="aw-time">${esc(win.start)}–${esc(win.end)}</td>
            <td>${esc(WINDOW_LABELS[win.name] ?? win.name)}</td>
            <td class="aw-mins${short ? " aw-short" : ""}">${win.minutes} min</td>
          </tr>`;
        })
        .join("");
      const warnings = day.warnings
        .map((warn) => `<p class="ap-warning-text">${esc(warn)}</p>`)
        .join("");
      return `<div class="aw-day">
        <h4>${esc(day.label)}</h4>
        <div class="ap-table-wrap"><table class="ap-table aw-table"><tbody>${rows}</tbody></table></div>
        ${warnings}
      </div>`;
    })
    .join("");

  section.innerHTML = `
    <h3>Work windows</h3>
    <p class="muted">
      Which job may use the provider, and when — derived from the scan, daily and
      weekly times above, in ${esc(w.timezone)}. Nothing here is set
      directly: move a brief and every window moves with it. A window under
      ${w.minWindowMinutes} minutes is flagged.
    </p>
    <div class="aw-days">${days}</div>`;
  return section;
}

/** The v6 backfill: what it has classified, what is stopping it, and a way in.
 *
 * Two things it does that a progress bar alone would not, and both exist
 * because a job that stops twice a day by design is otherwise indistinguishable
 * from one that has broken:
 *
 *  - it names **why** it is not running, in the schedule's words or the
 *    budget's, rather than showing "paused";
 *  - it puts the ETA in *working* time. A backfill paused overnight has not
 *    slowed down, and a wall-clock estimate would say it had.
 *
 * The histograms are of the whole corpus, not of this run: the question while a
 * backfill is going is what the corpus is turning into, which two hours of it
 * cannot answer. */
function renderBackfillSection(): HTMLDivElement {
  const section = document.createElement("div");
  // `ab-section` widens the 480px column the other settings panels sit in: the
  // spot-check tables are six columns and do not fit in it.
  section.className = "ap-settings-section ab-section";

  if (!_backfill) {
    section.innerHTML = `<h3>v6 backfill</h3>
      <p class="muted">Could not read the backfill's progress.</p>`;
    return section;
  }

  const b = _backfill;
  // The bar measures the *corpus*, not the run: a finished backfill started
  // again resets the job's own tally, and a progress bar that dropped to zero
  // on a fully classified corpus would be reporting the opposite of the truth.
  const corpus = b.corpus ?? null;
  const pct = corpus ? Math.min(100, Math.round((b.classified / corpus) * 100)) : 0;
  const running = b.runningHere;

  // The one sentence somebody reads first. A paused job is the schedule
  // working; only `failed` wants attention.
  let state = `<span class="ab-state ab-${esc(b.status)}">${esc(b.status)}</span>`;
  if (b.reason) state += ` <span class="muted">${esc(b.reason)}</span>`;

  const gate =
    b.window && !b.window.allowed
      ? `<p class="ap-warning-text">Not running: ${esc(b.window.reason)}.</p>`
      : "";

  const budget = b.budget
    ? `<p class="muted">
         Spending from <code>${esc(b.budget.bucket)}</code> —
         ${b.budget.used}${b.budget.cap ? ` of ${b.budget.cap}` : ""} requests on the
         ${esc(b.budget.quotaDay)} quota day (${esc(b.budget.resetTz)}).
         ${b.budget.cap
           ? `The backfill may reach ${b.budget.ceiling}; the rest is held for the briefs.`
           : "Nothing is capped — set a daily cap above to hold requests back for the briefs."}
       </p>`
    : "";

  const bars = (rows: { label: string; count: number; share: number }[]) =>
    rows.length === 0
      ? `<p class="muted">Nothing classified yet.</p>`
      : rows
          .map(
            (r) => `<div class="ab-bar-row">
              <span class="ab-bar-label">${esc(r.label)}</span>
              <span class="ab-bar"><span class="ab-bar-fill" style="width:${(r.share * 100).toFixed(1)}%"></span></span>
              <span class="ab-bar-count">${r.count} · ${(r.share * 100).toFixed(1)}%</span>
            </div>`
          )
          .join("");

  const lines = b.lines.length
    ? `<pre class="ab-log">${esc(b.lines.slice(-12).join("\n"))}</pre>`
    : "";

  const notice = _backfillNotice
    ? `<p class="ap-warning-text">${esc(_backfillNotice)}</p>`
    : "";

  section.innerHTML = `
    <h3>v6 backfill</h3>
    <p class="muted">
      Classifies signals collected before v6 from what is already stored —
      no article is refetched. It runs only in the windows above, only on the
      <code>backfill</code> role, and it stops at the edge of a window rather
      than through it. Stopping is normal; it picks itself back up.
    </p>
    ${notice}
    <p>${state}</p>
    <div class="ab-progress">
      <span class="ab-progress-fill" style="width:${pct}%"></span>
    </div>
    <p class="muted">
      ${b.classified}${corpus ? ` of ${corpus}` : ""} signals classified${
        b.remaining !== null ? ` — ${b.remaining} to go` : ""
      }.
      ${b.processed || b.skipped
        ? `This run has done ${b.processed}${b.skipped ? `, skipping ${b.skipped}` : ""}.`
        : ""}
      ${running ? `About ${esc(b.eta)} of working time left, at the rate this process has seen.` : ""}
    </p>
    ${gate}
    ${budget}
    <div class="ap-row ab-actions">
      <button class="btn btn-sm" id="ab-start" ${running ? "disabled" : ""}>Start</button>
      <button class="btn btn-sm" id="ab-stop" ${running ? "" : "disabled"}>Stop</button>
      <button class="btn btn-sm" id="ab-dry">Dry run</button>
      <button class="btn btn-sm" id="ab-sample">Sample 20</button>
      <a class="btn btn-sm" id="ab-baseline" href="/api/admin/settings/backfill/baseline">Baseline report</a>
    </div>
    ${lines}
    <div class="ab-histograms">
      <div><h4>Signal types</h4>${bars(b.types)}</div>
      <div><h4>Claim statuses</h4>${bars(b.claimStatuses)}</div>
    </div>
    <div id="ab-dry-out">${renderDryRun()}</div>
    <div id="ab-sample-out">${renderBackfillSample()}</div>`;

  const refresh = async () => {
    await loadBackfill();
    const fresh = renderBackfillSection();
    section.replaceWith(fresh);
  };

  section.querySelector<HTMLButtonElement>("#ab-start")?.addEventListener("click", async (ev) => {
    const btn = ev.currentTarget as HTMLButtonElement;
    btn.disabled = true;
    _backfillNotice = "";
    try {
      const out = await api<{ ok: boolean; message: string }>(
        "/admin/settings/backfill/start", { method: "POST" }
      );
      if (!out.ok) _backfillNotice = out.message;
      else startBackfillPoll();
    } catch (err) {
      _backfillNotice = err instanceof ApiError ? err.message : "Could not start it.";
    }
    await refresh();
  });

  section.querySelector<HTMLButtonElement>("#ab-stop")?.addEventListener("click", async (ev) => {
    (ev.currentTarget as HTMLButtonElement).disabled = true;
    _backfillNotice = "";
    try {
      await api("/admin/settings/backfill/stop", { method: "POST" });
      _backfillNotice = "It will stop after the batch it is on.";
    } catch (err) {
      _backfillNotice = err instanceof ApiError ? err.message : "Could not stop it.";
    }
    await refresh();
  });

  section.querySelector<HTMLButtonElement>("#ab-dry")?.addEventListener("click", async (ev) => {
    const btn = ev.currentTarget as HTMLButtonElement;
    btn.disabled = true;
    btn.textContent = "Classifying…";
    _backfillNotice = "";
    try {
      _backfillDryRun = await api<BackfillDryRun>(
        "/admin/settings/backfill/dry-run", { method: "POST" }
      );
    } catch (err) {
      _backfillNotice = err instanceof ApiError ? err.message : "The dry run failed.";
    }
    await refresh();
  });

  section.querySelector<HTMLButtonElement>("#ab-sample")?.addEventListener("click", async (ev) => {
    (ev.currentTarget as HTMLButtonElement).disabled = true;
    try {
      _backfillSample = await api<BackfillSampleRow[]>("/admin/settings/backfill/sample?n=20");
    } catch {
      _backfillSample = [];
    }
    await refresh();
  });

  return section;
}

/** Poll while it is running here, and stop the moment it is not.
 *
 * Deliberately not an SSE stream: the backfill runs for days and nobody keeps
 * the tab open for it. A 10-second poll costs one small read and answers the
 * only question somebody has while watching. */
function startBackfillPoll(): void {
  if (_backfillPoll !== null) return;
  _backfillPoll = window.setInterval(async () => {
    if (_activeTab !== "settings") {
      stopBackfillPoll();
      return;
    }
    await loadBackfill();
    if (!_backfill?.runningHere) stopBackfillPoll();
    const existing = document.querySelector<HTMLDivElement>(".ap-settings-section .ab-progress");
    if (existing?.parentElement) existing.parentElement.replaceWith(renderBackfillSection());
  }, 10_000);
}

function stopBackfillPoll(): void {
  if (_backfillPoll !== null) {
    window.clearInterval(_backfillPoll);
    _backfillPoll = null;
  }
}

/** What the dry run would have written. **It wrote nothing**, and the panel
 * says so every time rather than once in the button's tooltip. */
function renderDryRun(): string {
  if (!_backfillDryRun) return "";
  const d = _backfillDryRun;
  if (d.error) return `<p class="ap-warning-text">Dry run: ${esc(d.error)}</p>`;

  const rows = d.rows
    .map(
      (r) => `<tr>
        <td>${esc(r.dateAdded)}</td>
        <td>${esc(r.title ?? "")}</td>
        <td><code>${esc(r.signalType ?? "—")}</code></td>
        <td><code>${esc(r.claimStatus ?? "—")}</code>${
          r.claimTrigger ? ` <span class="muted">(${esc(r.claimTrigger)})</span>` : ""
        }</td>
        <td>${esc(r.horizonBefore ?? "—")} → ${esc(r.horizonAfter ?? "—")}</td>
        <td>${esc(r.story)}</td>
      </tr>`
    )
    .join("");

  return `
    <h4>Dry run — ${d.classified} of ${d.sampled} classified, nothing written</h4>
    <p class="muted">
      A random sample across the whole corpus, classified and then rolled back.
      No signal was changed and no story was opened.
    </p>
    <div class="ap-table-wrap"><table class="ap-table ab-dry-table">
      <thead><tr>
        <th>Collected</th><th>Title</th><th>Type</th><th>Claim</th>
        <th>Horizon</th><th>Story</th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table></div>
    ${d.notes.length ? `<pre class="ab-log">${esc(d.notes.slice(-10).join("\n"))}</pre>` : ""}`;
}

/** Twenty classified signals at random, for a spot check. A fresh draw each
 * press — a stable sample is one somebody stops reading. */
function renderBackfillSample(): string {
  if (!_backfillSample) return "";
  if (_backfillSample.length === 0)
    return `<p class="muted">Nothing is classified yet.</p>`;
  const rows = _backfillSample
    .map(
      (r) => `<tr>
        <td>${esc(r.ref ?? "")}</td>
        <td>${esc(r.title ?? "")}</td>
        <td><code>${esc(r.signalType ?? "—")}</code></td>
        <td><code>${esc(r.claimStatus ?? "—")}</code></td>
        <td>${esc(r.horizonLegacy ?? "—")} → ${esc(r.horizon ?? "—")}</td>
        <td>${r.novelty === null ? "—" : r.novelty.toFixed(2)}</td>
        <td class="ab-rationale">${esc(r.rationale ?? "")}</td>
      </tr>`
    )
    .join("");
  return `
    <h4>Twenty at random</h4>
    <div class="ap-table-wrap"><table class="ap-table ab-sample-table">
      <thead><tr>
        <th>Ref</th><th>Title</th><th>Type</th><th>Claim</th>
        <th>Horizon</th><th>Novelty</th><th>Rationale</th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table></div>`;
}

const WINDOW_LABELS: Record<string, string> = {
  protected_daily: "Scan and daily brief — nothing else runs",
  weekly_final_a: "Weekly, first pass",
  weekly_final_b: "Weekly, second pass",
  rolling_summaries: "Rolling story summaries",
  backfill_allowed: "Free for the backfill",
};

/** How much of each model's day is gone, and who may spend the rest.
 *
 * The counter is per model because that is how the provider meters it, and the
 * day is counted in the **provider's** zone — which is why the zone is on the
 * heading rather than buried. On an Asia/Singapore install this day rolls over
 * mid-afternoon, and a panel that did not say so would look like it reset at
 * random. */
function renderDailyBudgetSection(): HTMLDivElement {
  const section = document.createElement("div");
  section.className = "ap-settings-section";

  if (!_dailyBudget) {
    section.innerHTML = `<h3>Daily request budget</h3>
      <p class="muted">Could not read the daily budget.</p>`;
    return section;
  }

  const b = _dailyBudget;
  // Rounded to the minute first, then split — otherwise 82,790 seconds reads as
  // "22h 60m", because the remainder rounds up into a full hour the hour count
  // has already been taken from.
  const totalMins = Math.round(b.secondsUntilReset / 60);
  const hours = Math.floor(totalMins / 60);
  const mins = totalMins % 60;

  const rows = b.models
    .map((m) => {
      const share = m.cap > 0 ? m.used / m.cap : null;
      const cls =
        share === null ? "" : share >= 1 ? " ab-over" : share >= 0.7 ? " ab-near" : "";
      const spent =
        m.cap > 0
          ? `${m.used.toLocaleString()} of ${m.cap.toLocaleString()}`
          : `${m.used.toLocaleString()} <span class="muted">— uncapped</span>`;
      return `<tr>
        <td>${esc(m.label)}</td>
        <td class="ab-num${cls}">${spent}</td>
        <td class="ab-num muted">${m.cap > 0 ? m.reservedCeiling.toLocaleString() : "—"}</td>
        <td><input type="number" min="0" class="ap-num-input ab-cap-input"
                   data-bucket="${esc(m.bucket)}" value="${m.cap || ""}"
                   placeholder="none" /></td>
      </tr>`;
    })
    .join("");

  section.innerHTML = `
    <h3>Daily request budget</h3>
    <p class="muted">
      Counted per model, on the <strong>provider's</strong> day — currently
      ${esc(b.quotaDay)} in ${esc(b.resetTz)}, resetting in
      ${hours}h ${mins}m. That is not this app's time zone, so the day rolls over
      at whatever local hour midnight falls in there.
    </p>
    <p class="muted">
      Leave a cap blank for no limit, which is how this ships — nothing is capped
      until you set a number. The reserve holds
      <strong>${b.reservedRoles.map(esc).join(", ") || "nothing"}</strong>
      out of the last slice of each allowance, so a long job cannot spend the
      requests the morning's brief still needs.
    </p>
    <div class="ap-table-wrap"><table class="ap-table ab-table">
      <thead><tr>
        <th>Model</th><th>Used today</th><th>Reserved roles stop at</th><th>Cap</th>
      </tr></thead>
      <tbody>${rows}</tbody>
    </table></div>
    <div class="ab-controls">
      <label>Reserve
        <input type="number" min="0" max="100" id="ab-reserve"
               class="ap-num-input" value="${b.reservePercent}" /> %
      </label>
      <label>Reset zone
        <input type="text" id="ab-tz" class="ap-text-input"
               value="${esc(b.resetTz)}" />
      </label>
      <span class="save-msg" id="ab-msg"></span>
      <button class="primary" id="ab-save">Save</button>
    </div>`;

  section.querySelector<HTMLButtonElement>("#ab-save")!.addEventListener(
    "click",
    async () => {
      const msg = section.querySelector<HTMLSpanElement>("#ab-msg")!;
      const caps: Record<string, number> = {};
      section.querySelectorAll<HTMLInputElement>(".ab-cap-input").forEach((el) => {
        // A blank box means "no cap", which the server stores by removing the
        // entry. Sending 0 says the same thing and is what an emptied field
        // parses to.
        caps[el.dataset.bucket!] = Math.max(0, Number(el.value) || 0);
      });
      try {
        _dailyBudget = await api<DailyBudget>("/admin/settings/daily-budget", {
          method: "PUT",
          body: {
            caps,
            reservePercent: Number(
              section.querySelector<HTMLInputElement>("#ab-reserve")!.value,
            ),
            resetTz: section.querySelector<HTMLInputElement>("#ab-tz")!.value.trim(),
          },
        });
        if (_activeTab === "settings") void refreshSettingsTab();
      } catch (err) {
        msg.textContent = err instanceof ApiError ? err.message : "Save failed";
        msg.className = "save-msg save-err";
      }
    },
  );

  return section;
}

function renderRateLimitSection(): HTMLElement {
  const section = document.createElement("div");
  section.className = "ap-settings-section ap-rpm-section";

  const hint = `
    <p class="muted ap-key-hint">
      Calls are paced against both limits before they are sent, so a free tier is
      not blown through in the first minute of a scan. <strong>0 means no
      pacing.</strong> AI Studio reports the real numbers for your key, per model
      — worth copying in, because <strong>tokens per minute is usually the one
      that bites</strong>: requests can sit comfortably under their ceiling while
      tokens run at twice theirs, and a brief too large for one minute never
      arrives at all.
    </p>`;

  if (_rateLimits.length === 0) {
    section.innerHTML = `<h3>Provider rate limits</h3>${hint}<p class="muted">Could not load the current limits.</p>`;
    return section;
  }

  const rows = _rateLimits
    .map((r) => {
      const source = esc(rateLimitSource(r));
      return `
        <div class="ap-rpm-row">
          <label class="ap-rpm-label" for="rpm-${esc(r.provider)}">${esc(r.providerLabel)}</label>
          <input type="number" id="rpm-${esc(r.provider)}" class="ap-rpm-input"
                 data-provider="${esc(r.provider)}" min="0" max="10000" step="1"
                 value="${r.rpm}" />
          <span class="ap-rpm-unit">req/min</span>
          <span class="muted ap-rpm-source">${source}</span>
        </div>
        <div class="ap-rpm-row">
          <label class="ap-rpm-label" for="tpm-${esc(r.provider)}"></label>
          <input type="number" id="tpm-${esc(r.provider)}" class="ap-tpm-input"
                 data-provider="${esc(r.provider)}" min="0" max="10000000" step="1000"
                 value="${r.tpm}" />
          <span class="ap-rpm-unit">tokens/min</span>
          <span class="muted ap-rpm-source">${esc(tpmSource(r))}</span>
        </div>`;
    })
    .join("");

  section.innerHTML = `
    <h3>Provider rate limits</h3>
    ${hint}
    <div class="ap-rpm-rows">${rows}</div>
    <p class="muted ap-settings-err" id="ap-rpm-err"></p>
    <div class="ap-settings-actions">
      <button class="primary" id="ap-rpm-save">Save</button>
      <span class="muted" id="ap-rpm-saved" style="display:none">Saved.</span>
    </div>`;

  section.querySelector("#ap-rpm-save")!.addEventListener("click", async () => {
    const errEl = section.querySelector<HTMLElement>("#ap-rpm-err")!;
    const savedEl = section.querySelector<HTMLElement>("#ap-rpm-saved")!;
    errEl.textContent = "";
    savedEl.style.display = "none";

    const limits: Record<string, { rpm?: number; tpm?: number }> = {};
    // Both dimensions in one payload. The backend merges rather than replaces,
    // so a provider whose row is untouched keeps what it had.
    for (const [selector, field] of [
      [".ap-rpm-input", "rpm"],
      [".ap-tpm-input", "tpm"],
    ] as const) {
      for (const input of section.querySelectorAll<HTMLInputElement>(selector)) {
        const value = Number(input.value);
        if (!Number.isInteger(value) || value < 0) {
          errEl.textContent = "Rate limits are whole numbers, 0 or more.";
          return;
        }
        const provider = input.dataset["provider"]!;
        (limits[provider] ??= {})[field] = value;
      }
    }

    try {
      const updated = await api<ProviderRateLimit[]>("/admin/settings/rate-limits", {
        method: "PUT",
        body: { limits },
      });
      // Patched from the response rather than re-rendering the tab: saving
      // changes which source each row reports, and a row still claiming
      // "Default" would be a lie — but a re-render also destroys the "Saved."
      // it was supposed to confirm, leaving the click looking ignored.
      _rateLimits = updated;
      for (const row of updated) {
        const input = section.querySelector<HTMLInputElement>(`.ap-rpm-input[data-provider="${row.provider}"]`);
        if (input) input.value = String(row.rpm);
        const source = input?.parentElement?.querySelector<HTMLElement>(".ap-rpm-source");
        if (source) source.textContent = rateLimitSource(row);
        const tInput = section.querySelector<HTMLInputElement>(`.ap-tpm-input[data-provider="${row.provider}"]`);
        if (tInput) tInput.value = String(row.tpm);
        const tSource = tInput?.parentElement?.querySelector<HTMLElement>(".ap-rpm-source");
        if (tSource) tSource.textContent = tpmSource(row);
      }
      savedEl.style.display = "";
    } catch (err) {
      errEl.textContent = err instanceof ApiError ? err.message : "Save failed.";
    }
  });

  return section;
}

// ── Feed coverage section ─────────────────────────────────────────────────

function renderFeedCoverageSection(): HTMLElement {
  const section = document.createElement("div");
  section.className = "ap-settings-section ap-feeds-section";

  const hint = `
    <p class="muted ap-feeds-hint">
      The dashboard panels and the collector read two different feed lists. A feed
      the dashboard shows but the collector never visits can be read here and still
      never become a signal — which is what happened, unnoticed, for months.
    </p>
  `;

  if (!_feedCoverage) {
    section.innerHTML = `<h3>Feed Coverage</h3>${hint}<p class="muted">Could not load the feed comparison.</p>`;
    return section;
  }

  const r = _feedCoverage;
  const missing = r.missing.length;
  const badgeCls = missing === 0 ? "ap-status-ok" : "ap-status-warn";
  const badgeText =
    missing === 0
      ? `All ${r.dashboardFeedCount} dashboard feeds are collected`
      : `${missing} dashboard feed${missing === 1 ? "" : "s"} not collected`;

  section.innerHTML = `
    <h3>Feed Coverage</h3>
    ${hint}
    <span class="ap-status-badge ${badgeCls}">${esc(badgeText)}</span>
    <div class="ap-key-rows">
      <div class="ap-key-row">
        <span class="ap-key-row-label">Collector feeds</span>
        <span class="ap-key-row-value">${r.collectorFeedCount}</span>
      </div>
      <div class="ap-key-row">
        <span class="ap-key-row-label">Dashboard feeds</span>
        <span class="ap-key-row-value">${r.dashboardFeedCount}</span>
      </div>
    </div>
  `;

  if (missing > 0) {
    const table = document.createElement("table");
    table.className = "ap-table ap-feeds-table";
    table.innerHTML = `
      <thead><tr><th>Feed</th><th>Panel</th><th>URL</th></tr></thead>
      <tbody></tbody>
    `;
    const tbody = table.querySelector("tbody")!;
    r.missing.forEach((feed) => {
      const tr = document.createElement("tr");
      tr.innerHTML = `
        <td>${esc(feed.name)}</td>
        <td>${esc(feed.panel)}</td>
        <td class="ap-td-mono">${esc(feed.url)}</td>
      `;
      tbody.appendChild(tr);
    });
    // Wrapped, like the users, workstream and watch-area tables. Appended bare
    // it sat inside `.ap-page { overflow-x: hidden }`, so the URL column was
    // clipped with no scrollbar and no way to reach it.
    const wrap = document.createElement("div");
    wrap.className = "ap-table-wrap";
    wrap.appendChild(table);
    section.appendChild(wrap);
  }

  const actions = document.createElement("div");
  actions.className = "ap-settings-actions";

  const addBtn = document.createElement("button");
  addBtn.className = "primary";
  addBtn.textContent = `Add to collector`;
  addBtn.disabled = missing === 0;
  addBtn.addEventListener("click", async () => {
    addBtn.disabled = true;
    addBtn.textContent = "Adding…";
    _feedCoverageNotice = "";
    try {
      const updated = await api<FeedCoverageReport>("/admin/settings/feed-coverage/sync", {
        method: "POST",
      });
      _feedCoverage = updated;
      _feedCoverageNotice = `Added ${missing} feed${missing === 1 ? "" : "s"}. They are collected from the next run.`;
    } catch (err) {
      _feedCoverageNotice = err instanceof ApiError ? err.message : "Could not update the feed list.";
    }
    await refreshSettingsTab();
  });

  actions.appendChild(addBtn);

  if (_feedCoverageNotice) {
    const notice = document.createElement("span");
    notice.className = "muted";
    notice.textContent = _feedCoverageNotice;
    actions.appendChild(notice);
  }

  section.appendChild(actions);
  return section;
}


// ── Feed health section ───────────────────────────────────────────────────
//
// Coverage above asks whether the two feed lists agree. This asks whether the
// feeds work — the question nothing answered, which is how seven dead feeds
// went unnoticed for months and 21 regional ones went unchecked entirely.

const FEED_STATUS_LABEL: Record<string, string> = {
  ok: "OK",
  stale: "Stale",
  fail: "Failing",
  unknown: "Never checked",
};

const FEED_STATUS_CLASS: Record<string, string> = {
  ok: "ap-status-ok",
  stale: "ap-status-warn",
  fail: "ap-status-error",
  unknown: "ap-status-muted",
};

function renderFeedHealthSection(): HTMLElement {
  const section = document.createElement("div");
  section.className = "ap-settings-section ap-feeds-section";

  const hint = `
    <p class="muted ap-feeds-hint">
      Whether each configured feed actually answered. Recorded on every collection
      run at no extra cost — the run is already fetching all of them — so a feed
      that dies now shows up here instead of silently going missing from the corpus.
      <strong>Stale</strong> means it responded but has published nothing recent.
    </p>
  `;

  if (!_feedHealth) {
    section.innerHTML = `<h3>Feed Health</h3>${hint}<p class="muted">Could not load feed health.</p>`;
    return section;
  }

  const r = _feedHealth;
  const broken = r.failCount;
  const badgeCls = broken > 0 ? "ap-status-error" : r.unknownCount > 0 ? "ap-status-warn" : "ap-status-ok";
  const badgeText =
    broken > 0
      ? `${broken} feed${broken === 1 ? "" : "s"} failing`
      : r.unknownCount > 0
        ? `${r.unknownCount} feed${r.unknownCount === 1 ? "" : "s"} never checked`
        : `All ${r.okCount} feeds healthy`;

  section.innerHTML = `
    <h3>Feed Health</h3>
    ${hint}
    <span class="ap-status-badge ${badgeCls}">${esc(badgeText)}</span>
    <div class="ap-key-rows">
      <div class="ap-key-row">
        <span class="ap-key-row-label">OK / Stale / Failing / Never checked</span>
        <span class="ap-key-row-value">${r.okCount} / ${r.staleCount} / ${r.failCount} / ${r.unknownCount}</span>
      </div>
      <div class="ap-key-row">
        <span class="ap-key-row-label">Last checked</span>
        <span class="ap-key-row-value">${r.lastCheckedAt ? esc(fmtDateTime(r.lastCheckedAt)) : "never"}</span>
      </div>
    </div>
  `;

  const table = document.createElement("table");
  table.className = "ap-table ap-feeds-table";
  // Four columns, not five. The detail is a full exception string and gets
  // squeezed to one character per line as a column of its own; under the feed
  // name it has the width to read as a sentence, and the table stops needing
  // horizontal scrolling to see the thing that explains the status.
  table.innerHTML = `
    <thead><tr><th>Feed</th><th>Status</th><th>Failing for</th><th>Last OK</th></tr></thead>
    <tbody></tbody>
  `;
  const tbody = table.querySelector("tbody")!;
  r.feeds.forEach((feed) => {
    const tr = document.createElement("tr");
    // The detail line already says what happened on a healthy or stale feed;
    // the stored error is only worth showing where it is the explanation.
    const detail = feed.status === "fail" && feed.lastError ? feed.lastError : feed.detail ?? "";
    const streak =
      feed.consecutiveFailures > 0
        ? `${feed.consecutiveFailures} check${feed.consecutiveFailures === 1 ? "" : "s"}`
        : "—";
    tr.innerHTML = `
      <td>
        <div class="ap-feed-name">${esc(feed.name)}${
          feed.inConfig ? "" : ' <span class="muted">(removed from config)</span>'
        }</div>
        ${detail ? `<div class="ap-feed-detail">${esc(detail)}</div>` : ""}
      </td>
      <td><span class="ap-status-badge ${FEED_STATUS_CLASS[feed.status] ?? "ap-status-muted"}">${esc(
        FEED_STATUS_LABEL[feed.status] ?? feed.status,
      )}</span></td>
      <td>${esc(streak)}</td>
      <td>${feed.lastOkAt ? esc(fmtDateTime(feed.lastOkAt)) : "never"}</td>
    `;
    tbody.appendChild(tr);
  });
  // Wrapped for the same reason as the coverage table above: bare inside
  // `.ap-page { overflow-x: hidden }` the last column is clipped unreachably.
  const wrap = document.createElement("div");
  wrap.className = "ap-table-wrap";
  wrap.appendChild(table);
  section.appendChild(wrap);

  const actions = document.createElement("div");
  actions.className = "ap-settings-actions";

  const checkBtn = document.createElement("button");
  checkBtn.className = "primary";
  checkBtn.disabled = r.checking;
  checkBtn.textContent = r.checking ? "Checking…" : "Check now";
  checkBtn.addEventListener("click", async () => {
    checkBtn.disabled = true;
    checkBtn.textContent = "Checking…";
    _feedHealthNotice = "";
    try {
      _feedHealth = await api<FeedHealthReport>("/admin/settings/feed-health/check", {
        method: "POST",
      });
      startFeedHealthPolling();
    } catch (err) {
      _feedHealthNotice = err instanceof ApiError ? err.message : "Could not start the check.";
      await refreshSettingsTab();
    }
  });
  actions.appendChild(checkBtn);

  if (_feedHealthNotice) {
    const notice = document.createElement("span");
    notice.className = "muted";
    notice.textContent = _feedHealthNotice;
    actions.appendChild(notice);
  }

  section.appendChild(actions);

  if (r.checking) startFeedHealthPolling();
  return section;
}

/** Re-read health while a check runs, so rows fill in as they land. */
function startFeedHealthPolling(): void {
  if (_feedHealthPoll !== null) return;
  _feedHealthPoll = window.setInterval(async () => {
    await loadFeedHealth();
    if (!_feedHealth?.checking) {
      stopFeedHealthPolling();
    }
    // Only redraw while the settings tab is the one on screen — refreshing a
    // tab the operator has navigated away from would yank them back.
    if (_activeTab === "settings") await refreshSettingsTab();
  }, 2000);
}

function stopFeedHealthPolling(): void {
  if (_feedHealthPoll !== null) {
    window.clearInterval(_feedHealthPoll);
    _feedHealthPoll = null;
  }
}


// ── Junk (placeholder) signals section ────────────────────────────────────

function renderJunkSection(): HTMLElement {
  const section = document.createElement("div");
  section.className = "ap-settings-section ap-junk-section";

  const hint = `
    <p class="muted ap-junk-hint">
      Rows the scanner stored without a real analysis pass — the raw excerpt as the
      summary, a placeholder implication, dummy STEEPLES scores, no wikilinks. They
      skew the graph, Pulse, search and the daily brief. Deleting them does not
      re-collect the articles.
    </p>
  `;

  if (!_stubReport) {
    section.innerHTML = `<h3>Junk Signals</h3>${hint}<p class="muted">Could not load the junk signal count.</p>`;
    return section;
  }

  const r = _stubReport;
  const pct = r.totalSignals > 0 ? (r.count / r.totalSignals) * 100 : 0;
  const badgeCls = r.count === 0 ? "ap-status-ok" : "ap-status-warn";
  const badgeText =
    r.count === 0
      ? "Corpus clean"
      : `${r.count} of ${r.totalSignals} signals (${pct.toFixed(1)}%)`;

  const dateRows = r.byDate
    .map(
      (d) =>
        `<div class="ap-key-row"><span class="ap-key-row-label">${esc(d.date)}</span>` +
        `<span class="ap-key-row-value">${d.count}</span></div>`
    )
    .join("");

  section.innerHTML = `
    <h3>Junk Signals</h3>
    ${hint}
    <span class="ap-status-badge ${badgeCls}">${esc(badgeText)}</span>
    ${r.count > 0 ? `<div class="ap-key-rows ap-junk-dates">${dateRows}</div>` : ""}
  `;

  if (r.count > 0) {
    const table = document.createElement("table");
    table.className = "ap-table ap-junk-table";
    table.innerHTML = `
      <thead><tr><th>Title</th><th>Source</th><th>Added</th></tr></thead>
      <tbody></tbody>
    `;
    const tbody = table.querySelector("tbody")!;
    r.preview.forEach((sig) => {
      const tr = document.createElement("tr");
      tr.innerHTML = `
        <td>${esc(sig.title)}${sig.annotated ? ' <span class="ap-junk-flag">annotated</span>' : ""}</td>
        <td>${esc(sig.source)}</td>
        <td class="ap-td-mono">${esc(sig.dateAdded)}</td>
      `;
      tbody.appendChild(tr);
    });
    // Wrapped for the same reason as the feed-coverage table above: the
    // date column was clipped and unreachable.
    const wrap = document.createElement("div");
    wrap.className = "ap-table-wrap";
    wrap.appendChild(table);
    section.appendChild(wrap);

    if (r.count > r.preview.length) {
      const more = document.createElement("p");
      more.className = "muted ap-junk-more";
      more.textContent = `Showing the ${r.preview.length} most recent — ${
        r.count - r.preview.length
      } more will also be deleted.`;
      section.appendChild(more);
    }
  }

  // Signals someone has commented on or filed under a workstream are worth a
  // deliberate second decision, so they are preserved unless opted out of.
  let deleteAnnotated = false;
  if (r.annotatedCount > 0) {
    const label = document.createElement("label");
    label.className = "ap-junk-keep-label";
    label.innerHTML = `
      <input type="checkbox" id="junk-include-annotated" />
      Also delete the ${r.annotatedCount} junk signal${r.annotatedCount === 1 ? "" : "s"}
      someone has commented on or assigned to a workstream
    `;
    section.appendChild(label);
    label.querySelector<HTMLInputElement>("#junk-include-annotated")!
      .addEventListener("change", (e) => {
        deleteAnnotated = (e.currentTarget as HTMLInputElement).checked;
      });
  }

  const actions = document.createElement("div");
  actions.className = "ap-settings-actions";

  const deleteBtn = document.createElement("button");
  deleteBtn.className = "destructive";
  deleteBtn.id = "junk-delete";
  deleteBtn.textContent = "Delete Junk Signals";
  deleteBtn.disabled = r.count === 0;
  deleteBtn.addEventListener("click", () =>
    openDeleteJunkModal(
      deleteAnnotated ? r.count : r.count - r.annotatedCount,
      deleteAnnotated
    )
  );

  const refreshBtn = document.createElement("button");
  refreshBtn.className = "ap-action-btn";
  refreshBtn.textContent = "Refresh";
  refreshBtn.addEventListener("click", () => {
    _stubNotice = "";
    void refreshSettingsTab();
  });

  actions.appendChild(deleteBtn);
  actions.appendChild(refreshBtn);

  if (_stubNotice) {
    const notice = document.createElement("span");
    notice.className = "muted";
    notice.textContent = _stubNotice;
    actions.appendChild(notice);
  }

  section.appendChild(actions);
  return section;
}

function openDeleteJunkModal(count: number, includeAnnotated: boolean): void {
  const overlay = createOverlay();
  overlay.innerHTML = `
    <div class="ap-modal" role="dialog" aria-modal="true" aria-label="Delete Junk Signals">
      <div class="ap-modal-header">
        <h2>Delete Junk Signals</h2>
        <button class="ap-modal-close" aria-label="Close">✕</button>
      </div>
      <div class="ap-modal-body">
        <p class="ap-warning-text">This permanently deletes
        <strong>${count}</strong> signal${count === 1 ? "" : "s"}, along with any
        comments and workstream links on them. This cannot be undone, and the
        articles are not re-collected.</p>
        <p class="ap-error" id="junk-del-error" style="display:none;"></p>
      </div>
      <div class="ap-modal-footer">
        <button class="ap-modal-cancel">Cancel</button>
        <button class="destructive" id="junk-del-confirm">Delete ${count}</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const close = () => overlay.remove();
  wireClose(overlay, close);

  const confirmBtn = overlay.querySelector<HTMLButtonElement>("#junk-del-confirm")!;
  confirmBtn.addEventListener("click", async () => {
    const errorEl = overlay.querySelector("#junk-del-error") as HTMLParagraphElement;
    errorEl.style.display = "none";
    confirmBtn.disabled = true;
    confirmBtn.textContent = "Deleting…";
    try {
      const res = await api<{ deleted: number; preserved: number }>(
        `/admin/maintenance/stub-signals?keep_annotated=${includeAnnotated ? "false" : "true"}`,
        { method: "DELETE" }
      );
      _stubNotice =
        `Deleted ${res.deleted} junk signal${res.deleted === 1 ? "" : "s"}.` +
        (res.preserved > 0 ? ` Preserved ${res.preserved} annotated.` : "");
      close();
      await refreshSettingsTab();
    } catch (err) {
      confirmBtn.disabled = false;
      confirmBtn.textContent = `Delete ${count}`;
      showErr(errorEl, err instanceof ApiError ? err.message : "Delete failed.");
    }
  });
}

// ── Users tab ──────────────────────────────────────────────────────────────

function renderUsersTab(container: HTMLElement): void {
  container.innerHTML = "";

  const toolbar = document.createElement("div");
  toolbar.className = "ap-toolbar";
  const addBtn = document.createElement("button");
  addBtn.className = "primary";
  addBtn.textContent = "+ Add User";
  addBtn.addEventListener("click", openAddUserModal);
  toolbar.appendChild(addBtn);
  container.appendChild(toolbar);

  if (_users.length === 0) {
    const empty = document.createElement("p");
    empty.className = "muted ap-empty-text";
    empty.textContent = "No users found.";
    container.appendChild(empty);
    return;
  }

  const tableWrap = document.createElement("div");
  tableWrap.className = "ap-table-wrap";
  tableWrap.innerHTML = `
    <table class="ap-table">
      <thead>
        <tr>
          <th>Username</th>
          <th>Role</th>
          <th>Created</th>
          <th>Last Login</th>
          <th>Telegram ID</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody id="ap-tbody"></tbody>
    </table>
  `;
  container.appendChild(tableWrap);

  const tbody = tableWrap.querySelector<HTMLTableSectionElement>("#ap-tbody")!;
  _users.forEach((user) => {
    const tr = document.createElement("tr");
    tr.innerHTML = `
      <td class="ap-td-username">${esc(user.username)}</td>
      <td><span class="ap-role-badge ap-role-${user.role}">${user.role}</span></td>
      <td class="ap-td-mono">${fmtDate(user.created_at)}</td>
      <td class="ap-td-mono">${user.last_login ? fmtDate(user.last_login) : "—"}</td>
      <td class="ap-td-mono ap-td-tg">${user.telegram_user_id ? esc(user.telegram_user_id) : '<span class="muted">—</span>'}</td>
      <td class="ap-td-actions"></td>
    `;
    const actionsCell = tr.querySelector<HTMLTableCellElement>(".ap-td-actions")!;

    const roleBtn = document.createElement("button");
    roleBtn.className = "ap-action-btn";
    roleBtn.textContent = "Change Role";
    roleBtn.addEventListener("click", () => openChangeRoleModal(user));

    const resetBtn = document.createElement("button");
    resetBtn.className = "ap-action-btn";
    resetBtn.textContent = "Reset Password";
    resetBtn.addEventListener("click", () => openResetPasswordModal(user));

    const tgBtn = document.createElement("button");
    tgBtn.className = "ap-action-btn";
    tgBtn.textContent = user.telegram_user_id ? "Edit Telegram" : "Link Telegram";
    tgBtn.addEventListener("click", () => openTelegramModal(user, tr));

    const deleteBtn = document.createElement("button");
    deleteBtn.className = "ap-action-btn ap-action-delete";
    deleteBtn.textContent = "Delete";
    deleteBtn.addEventListener("click", () => openDeleteUserModal(user));

    actionsCell.appendChild(roleBtn);
    actionsCell.appendChild(resetBtn);
    actionsCell.appendChild(tgBtn);
    actionsCell.appendChild(deleteBtn);
    tbody.appendChild(tr);
  });
}

// ── Add User Modal ─────────────────────────────────────────────────────────

function openAddUserModal(): void {
  const overlay = createOverlay();
  overlay.innerHTML = `
    <div class="ap-modal" role="dialog" aria-modal="true" aria-label="Add User">
      <div class="ap-modal-header">
        <h2>Add User</h2>
        <button class="ap-modal-close" aria-label="Close">✕</button>
      </div>
      <div class="ap-modal-body">
        <label class="ap-field-label" for="add-username">Username</label>
        <input type="text" id="add-username" class="ap-input" autocomplete="off" />
        <label class="ap-field-label" for="add-password">Password</label>
        <input type="password" id="add-password" class="ap-input" autocomplete="new-password" />
        <label class="ap-field-label" for="add-role">Role</label>
        <select id="add-role" class="ap-select">
          <option value="user">user</option>
          <option value="admin">admin</option>
        </select>
        <p class="ap-error" id="add-error" style="display:none;"></p>
      </div>
      <div class="ap-modal-footer">
        <button class="ap-modal-cancel">Cancel</button>
        <button class="primary" id="add-submit">Create User</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  (overlay.querySelector("#add-username") as HTMLInputElement).focus();

  const close = () => overlay.remove();
  wireClose(overlay, close);

  overlay.querySelector("#add-submit")!.addEventListener("click", async () => {
    const username = (overlay.querySelector("#add-username") as HTMLInputElement).value.trim();
    const password = (overlay.querySelector("#add-password") as HTMLInputElement).value;
    const role = (overlay.querySelector("#add-role") as HTMLSelectElement).value;
    const errorEl = overlay.querySelector("#add-error") as HTMLParagraphElement;
    errorEl.style.display = "none";

    if (!username) { showErr(errorEl, "Username is required."); return; }
    if (!password) { showErr(errorEl, "Password is required."); return; }

    try {
      const user = await api<UserRecord>("/users", {
        method: "POST",
        body: { username, password, role },
      });
      _users.push(user);
      close();
      refreshUsersTab();
    } catch (err) {
      showErr(errorEl, err instanceof ApiError ? err.message : "Failed to create user.");
    }
  });
}

// ── Change Role Modal ──────────────────────────────────────────────────────

function openChangeRoleModal(user: UserRecord): void {
  const newRole = user.role === "admin" ? "user" : "admin";
  const overlay = createOverlay();
  overlay.innerHTML = `
    <div class="ap-modal" role="dialog" aria-modal="true">
      <div class="ap-modal-header">
        <h2>Change Role</h2>
        <button class="ap-modal-close" aria-label="Close">✕</button>
      </div>
      <div class="ap-modal-body">
        <p>Change <strong>${esc(user.username)}</strong>'s role from
        <strong>${user.role}</strong> to <strong>${newRole}</strong>?</p>
        <p class="ap-error" id="role-error" style="display:none;"></p>
      </div>
      <div class="ap-modal-footer">
        <button class="ap-modal-cancel">Cancel</button>
        <button class="primary" id="role-confirm">Change Role</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const close = () => overlay.remove();
  wireClose(overlay, close);

  overlay.querySelector("#role-confirm")!.addEventListener("click", async () => {
    const errorEl = overlay.querySelector("#role-error") as HTMLParagraphElement;
    try {
      const updated = await api<UserRecord>(`/users/${user.id}/role`, {
        method: "PATCH",
        body: { role: newRole },
      });
      const idx = _users.findIndex((u) => u.id === user.id);
      if (idx >= 0) _users[idx] = updated;
      close();
      refreshUsersTab();
    } catch (err) {
      showErr(errorEl, err instanceof ApiError ? err.message : "Failed to change role.");
    }
  });
}

// ── Reset Password Modal ───────────────────────────────────────────────────

function openResetPasswordModal(user: UserRecord): void {
  const overlay = createOverlay();
  overlay.innerHTML = `
    <div class="ap-modal" role="dialog" aria-modal="true">
      <div class="ap-modal-header">
        <h2>Reset Password</h2>
        <button class="ap-modal-close" aria-label="Close">✕</button>
      </div>
      <div class="ap-modal-body">
        <label class="ap-field-label">User</label>
        <input type="text" class="ap-input" value="${esc(user.username)}" disabled />
        <label class="ap-field-label" for="reset-pwd">New Password</label>
        <input type="password" id="reset-pwd" class="ap-input" autocomplete="new-password" />
        <p class="ap-error" id="reset-error" style="display:none;"></p>
      </div>
      <div class="ap-modal-footer">
        <button class="ap-modal-cancel">Cancel</button>
        <button class="primary" id="reset-confirm">Save</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  (overlay.querySelector("#reset-pwd") as HTMLInputElement).focus();

  const close = () => overlay.remove();
  wireClose(overlay, close);

  overlay.querySelector("#reset-confirm")!.addEventListener("click", async () => {
    const newPassword = (overlay.querySelector("#reset-pwd") as HTMLInputElement).value;
    const errorEl = overlay.querySelector("#reset-error") as HTMLParagraphElement;
    if (!newPassword) { showErr(errorEl, "Password cannot be empty."); return; }
    try {
      await api(`/users/${user.id}/reset-password`, {
        method: "POST",
        body: { new_password: newPassword },
      });
      close();
    } catch (err) {
      showErr(errorEl, err instanceof ApiError ? err.message : "Failed to reset password.");
    }
  });
}

// ── Delete User Modal ──────────────────────────────────────────────────────

function openDeleteUserModal(user: UserRecord): void {
  const overlay = createOverlay();
  overlay.innerHTML = `
    <div class="ap-modal" role="dialog" aria-modal="true">
      <div class="ap-modal-header">
        <h2>Delete User</h2>
        <button class="ap-modal-close" aria-label="Close">✕</button>
      </div>
      <div class="ap-modal-body">
        <p class="ap-warning-text">This will permanently delete
        <strong>${esc(user.username)}</strong>. This action cannot be undone.</p>
        <p class="ap-error" id="del-error" style="display:none;"></p>
      </div>
      <div class="ap-modal-footer">
        <button class="ap-modal-cancel">Cancel</button>
        <button class="destructive" id="del-confirm">Delete</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const close = () => overlay.remove();
  wireClose(overlay, close);

  overlay.querySelector("#del-confirm")!.addEventListener("click", async () => {
    const errorEl = overlay.querySelector("#del-error") as HTMLParagraphElement;
    try {
      await api(`/users/${user.id}`, { method: "DELETE" });
      _users = _users.filter((u) => u.id !== user.id);
      close();
      refreshUsersTab();
    } catch (err) {
      showErr(errorEl, err instanceof ApiError ? err.message : "Failed to delete user.");
    }
  });
}

// ── Telegram Link Modal ────────────────────────────────────────────────────

function openTelegramModal(user: UserRecord, row: HTMLTableRowElement): void {
  const overlay = createOverlay();
  const linked = !!user.telegram_user_id;
  overlay.innerHTML = `
    <div class="ap-modal" role="dialog" aria-modal="true" aria-label="Telegram Link">
      <div class="ap-modal-header">
        <h2>Telegram Link — ${esc(user.username)}</h2>
        <button class="ap-modal-close" aria-label="Close">✕</button>
      </div>
      <div class="ap-modal-body">
        <p class="muted" style="font-size:13px;margin:0 0 var(--space-sm)">
          Ask the user to message <strong>/start</strong> to the Kassandra bot in Telegram —
          the bot will reply with their numeric Telegram ID.
        </p>
        <label class="ap-field-label" for="tg-id-input">Telegram User ID</label>
        <input
          type="text"
          id="tg-id-input"
          class="ap-input"
          placeholder="e.g. 123456789"
          value="${linked ? esc(user.telegram_user_id!) : ""}"
          autocomplete="off"
          inputmode="numeric"
        />
        <p class="ap-error" id="tg-error" style="display:none;"></p>
      </div>
      <div class="ap-modal-footer">
        ${linked ? '<button class="ap-action-btn ap-action-delete" id="tg-unlink">Unlink</button>' : ""}
        <button class="ap-modal-cancel">Cancel</button>
        <button class="primary" id="tg-save">Save</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  const input = overlay.querySelector<HTMLInputElement>("#tg-id-input")!;
  const errorEl = overlay.querySelector<HTMLParagraphElement>("#tg-error")!;
  input.focus();
  if (linked) input.select();

  const close = () => overlay.remove();
  wireClose(overlay, close);

  // Helper: update row in-place after a successful save
  function applyToRow(newId: string | null): void {
    user.telegram_user_id = newId;
    const tgCell = row.querySelector<HTMLTableCellElement>(".ap-td-tg");
    const tgBtn = Array.from(row.querySelectorAll<HTMLButtonElement>(".ap-action-btn"))
      .find((b) => b.textContent?.includes("Telegram"));
    if (tgCell) {
      tgCell.innerHTML = newId
        ? esc(newId)
        : '<span class="muted">—</span>';
    }
    if (tgBtn) {
      tgBtn.textContent = newId ? "Edit Telegram" : "Link Telegram";
    }
  }

  overlay.querySelector("#tg-save")!.addEventListener("click", async () => {
    errorEl.style.display = "none";
    const val = input.value.trim();
    if (!val) { showErr(errorEl, "Enter a Telegram User ID, or use Unlink to remove."); return; }
    if (!/^\d+$/.test(val)) { showErr(errorEl, "Telegram User IDs are numeric."); return; }
    try {
      await api(`/users/${user.id}/telegram`, {
        method: "PATCH",
        body: { telegram_user_id: val },
      });
      applyToRow(val);
      close();
    } catch (err) {
      showErr(errorEl, err instanceof ApiError ? err.message : "Failed to save.");
    }
  });

  overlay.querySelector("#tg-unlink")?.addEventListener("click", async () => {
    errorEl.style.display = "none";
    try {
      await api(`/users/${user.id}/telegram`, {
        method: "PATCH",
        body: { telegram_user_id: null },
      });
      applyToRow(null);
      close();
    } catch (err) {
      showErr(errorEl, err instanceof ApiError ? err.message : "Failed to unlink.");
    }
  });
}


// ── Shared modal helpers ───────────────────────────────────────────────────

function createOverlay(): HTMLDivElement {
  const overlay = document.createElement("div");
  overlay.className = "ap-modal-overlay";
  return overlay;
}

function wireClose(overlay: HTMLElement, close: () => void): void {
  overlay.querySelector(".ap-modal-close")?.addEventListener("click", close);
  overlay.querySelector(".ap-modal-cancel")?.addEventListener("click", close);
  overlay.addEventListener("click", (e) => {
    if (e.target === overlay) close();
  });
  // Escape, which every admin modal was missing — the rate-limit modal has had
  // it all along. On a phone that left a 28px close button and a sliver of
  // scrim as the only ways out.
  //
  // The listener retires itself once the overlay leaves the DOM rather than
  // being removed by each caller: there are fifteen `overlay.remove()` sites
  // and threading a teardown through all of them is a larger change than this
  // is worth. The cost is that a dead listener survives until the next
  // keypress, at which point it removes itself without acting.
  const onKey = (e: KeyboardEvent) => {
    if (!overlay.isConnected) {
      document.removeEventListener("keydown", onKey);
      return;
    }
    if (e.key === "Escape") close();
  };
  document.addEventListener("keydown", onKey);
}

function showErr(el: HTMLParagraphElement, msg: string): void {
  el.textContent = msg;
  el.style.display = "block";
}

function refreshUsersTab(): void {
  const content = document.getElementById("ap-content");
  if (content && _activeTab === "users") renderUsersTab(content);
}

// ── Workstreams tab ────────────────────────────────────────────────────────

function renderWorkstreamsTab(container: HTMLElement): void {
  container.innerHTML = "";

  const toolbar = document.createElement("div");
  toolbar.className = "ap-toolbar";

  const filterInput = document.createElement("input");
  filterInput.type = "text";
  filterInput.className = "ap-input ap-filter-input";
  filterInput.placeholder = "Filter by name…";
  filterInput.value = _wsFilter;
  filterInput.setAttribute("aria-label", "Filter workstreams");
  filterInput.addEventListener("input", () => {
    _wsFilter = filterInput.value;
    renderWsRows(tbody);
  });

  const addBtn = document.createElement("button");
  addBtn.className = "primary";
  addBtn.textContent = "+ Add Workstream";
  addBtn.addEventListener("click", () => openAddWorkstreamModal(refreshWorkstreamsTab));

  toolbar.appendChild(filterInput);
  toolbar.appendChild(addBtn);
  container.appendChild(toolbar);

  const tableWrap = document.createElement("div");
  tableWrap.className = "ap-table-wrap";
  tableWrap.innerHTML = `
    <table class="ap-table">
      <thead>
        <tr>
          <th>Name</th>
          <th>Signals</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody id="ap-ws-tbody"></tbody>
    </table>
  `;
  container.appendChild(tableWrap);

  const tbody = tableWrap.querySelector<HTMLTableSectionElement>("#ap-ws-tbody")!;
  renderWsRows(tbody);
}

function renderWsRows(tbody: HTMLTableSectionElement): void {
  tbody.innerHTML = "";
  const q = _wsFilter.toLowerCase();
  const filtered = q
    ? _workstreams.filter((ws) => ws.name.toLowerCase().includes(q))
    : _workstreams;

  if (filtered.length === 0) {
    const tr = document.createElement("tr");
    tr.innerHTML = `<td colspan="3" class="muted" style="padding:12px;text-align:center;">${
      _workstreams.length === 0 ? "No workstreams yet." : "No workstreams match the filter."
    }</td>`;
    tbody.appendChild(tr);
    return;
  }

  filtered.forEach((ws) => {
    const tr = document.createElement("tr");
    tr.innerHTML = `
      <td>${esc(ws.name)}</td>
      <td class="ap-td-mono">${ws.signalCount}</td>
      <td class="ap-td-actions"></td>
    `;
    const actionsCell = tr.querySelector<HTMLTableCellElement>(".ap-td-actions")!;

    const editBtn = document.createElement("button");
    editBtn.className = "ap-action-btn";
    editBtn.textContent = "Edit";
    editBtn.addEventListener("click", () => openEditDefinitionModal(ws, refreshWorkstreamsTab));

    const renameBtn = document.createElement("button");
    renameBtn.className = "ap-action-btn";
    renameBtn.textContent = "Rename";
    renameBtn.addEventListener("click", () => openRenameWorkstreamModal(ws, refreshWorkstreamsTab));

    const deleteBtn = document.createElement("button");
    deleteBtn.className = "ap-action-btn ap-action-delete";
    deleteBtn.textContent = "Delete";
    deleteBtn.addEventListener("click", () => openDeleteWorkstreamModal(ws, refreshWorkstreamsTab));

    actionsCell.appendChild(editBtn);
    actionsCell.appendChild(renameBtn);
    actionsCell.appendChild(deleteBtn);
    tbody.appendChild(tr);
  });
}

async function refreshWorkstreamsTab(): Promise<void> {
  const content = document.getElementById("ap-content");
  if (!content || _activeTab !== "workstreams") return;
  await loadWorkstreams();
  renderWorkstreamsTab(content);
}


// ── Add Workstream Modal ───────────────────────────────────────────────────

function openAddWorkstreamModal(onDone: () => void): void {
  const overlay = createOverlay();
  overlay.innerHTML = `
    <div class="ap-modal" role="dialog" aria-modal="true" aria-label="Add Workstream">
      <div class="ap-modal-header">
        <h2>Add Workstream</h2>
        <button class="ap-modal-close" aria-label="Close">✕</button>
      </div>
      <div class="ap-modal-body">
        <label class="ap-field-label" for="ws-add-name">Name</label>
        <input type="text" id="ws-add-name" class="ap-input" autocomplete="off" />
        <p class="ap-error" id="ws-add-error" style="display:none;"></p>
      </div>
      <div class="ap-modal-footer">
        <button class="ap-modal-cancel">Cancel</button>
        <button class="primary" id="ws-add-submit">Create</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  (overlay.querySelector("#ws-add-name") as HTMLInputElement).focus();

  const close = () => overlay.remove();
  wireClose(overlay, close);

  overlay.querySelector("#ws-add-submit")!.addEventListener("click", async () => {
    const name = (overlay.querySelector("#ws-add-name") as HTMLInputElement).value.trim();
    const errorEl = overlay.querySelector("#ws-add-error") as HTMLParagraphElement;
    errorEl.style.display = "none";
    if (!name) { showErr(errorEl, "Name is required."); return; }
    try {
      await api("/workstreams", { method: "POST", body: { name } });
      close();
      onDone();
    } catch (err) {
      showErr(errorEl, err instanceof ApiError ? err.message : "Failed to create workstream.");
    }
  });
}

// ── Rename Workstream Modal ────────────────────────────────────────────────

function openRenameWorkstreamModal(ws: WorkstreamRecord, onDone: () => void): void {
  const overlay = createOverlay();
  overlay.innerHTML = `
    <div class="ap-modal" role="dialog" aria-modal="true">
      <div class="ap-modal-header">
        <h2>Rename Workstream</h2>
        <button class="ap-modal-close" aria-label="Close">✕</button>
      </div>
      <div class="ap-modal-body">
        <label class="ap-field-label" for="ws-rename-input">New name</label>
        <input type="text" id="ws-rename-input" class="ap-input" value="${esc(ws.name)}" />
        <p class="ap-error" id="ws-rename-error" style="display:none;"></p>
      </div>
      <div class="ap-modal-footer">
        <button class="ap-modal-cancel">Cancel</button>
        <button class="primary" id="ws-rename-confirm">Rename</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);
  const input = overlay.querySelector<HTMLInputElement>("#ws-rename-input")!;
  input.select();

  const close = () => overlay.remove();
  wireClose(overlay, close);

  overlay.querySelector("#ws-rename-confirm")!.addEventListener("click", async () => {
    const newName = input.value.trim();
    const errorEl = overlay.querySelector("#ws-rename-error") as HTMLParagraphElement;
    errorEl.style.display = "none";
    if (!newName) { showErr(errorEl, "Name cannot be empty."); return; }
    try {
      await api(`/workstreams/${ws.id}`, { method: "PATCH", body: { new_name: newName } });
      close();
      onDone();
    } catch (err) {
      showErr(errorEl, err instanceof ApiError ? err.message : "Rename failed.");
    }
  });
}

// ── Edit Workstream Definition Modal ──────────────────────────────────────

function openEditDefinitionModal(ws: WorkstreamRecord, onDone: () => void): void {
  const overlay = createOverlay();
  overlay.innerHTML = `
    <div class="ap-modal ap-modal-wide" role="dialog" aria-modal="true" aria-label="Edit Workstream Definition">
      <div class="ap-modal-header">
        <h2>Edit Definition — ${esc(ws.name)}</h2>
        <button class="ap-modal-close" aria-label="Close">✕</button>
      </div>
      <div class="ap-modal-body">
        <p class="muted ap-def-hint">Describe what this workstream covers in plain English. This context is injected into the LLM prompt when generating a scoped report.</p>
        <textarea
          id="ws-def-textarea"
          class="ap-input ap-def-textarea"
          placeholder="e.g. This workstream tracks regulatory and policy developments around artificial intelligence, including EU AI Act updates, US executive orders, and international governance frameworks…"
          spellcheck="true"
        ></textarea>
        <p class="ap-error" id="ws-def-error" style="display:none;"></p>
      </div>
      <div class="ap-modal-footer">
        <button class="ap-modal-cancel">Cancel</button>
        <button class="primary" id="ws-def-submit">Save</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const textarea = overlay.querySelector<HTMLTextAreaElement>("#ws-def-textarea")!;
  textarea.value = ws.definition ?? "";
  textarea.focus();

  const close = () => overlay.remove();
  wireClose(overlay, close);

  overlay.querySelector("#ws-def-submit")!.addEventListener("click", async () => {
    const definition = textarea.value.trim() || null;
    const errorEl = overlay.querySelector("#ws-def-error") as HTMLParagraphElement;
    errorEl.style.display = "none";
    try {
      await api(`/workstreams/${ws.id}/definition`, {
        method: "PUT",
        body: { definition },
      });
      ws.definition = definition;
      close();
      onDone();
    } catch (err) {
      showErr(errorEl, err instanceof ApiError ? err.message : "Failed to save definition.");
    }
  });
}

// ── Delete Workstream Modal ────────────────────────────────────────────────

function openDeleteWorkstreamModal(ws: WorkstreamRecord, onDone: () => void): void {
  const overlay = createOverlay();
  overlay.innerHTML = `
    <div class="ap-modal" role="dialog" aria-modal="true">
      <div class="ap-modal-header">
        <h2>Delete Workstream</h2>
        <button class="ap-modal-close" aria-label="Close">✕</button>
      </div>
      <div class="ap-modal-body">
        <p class="ap-warning-text">Delete <strong>${esc(ws.name)}</strong>?
        This will remove the workstream from ${ws.signalCount} signal${ws.signalCount === 1 ? "" : "s"}.
        This cannot be undone.</p>
        <p class="ap-error" id="ws-del-error" style="display:none;"></p>
      </div>
      <div class="ap-modal-footer">
        <button class="ap-modal-cancel">Cancel</button>
        <button class="destructive" id="ws-del-confirm">Delete</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const close = () => overlay.remove();
  wireClose(overlay, close);

  overlay.querySelector("#ws-del-confirm")!.addEventListener("click", async () => {
    const errorEl = overlay.querySelector("#ws-del-error") as HTMLParagraphElement;
    try {
      await api(`/workstreams/${ws.id}`, { method: "DELETE" });
      close();
      onDone();
    } catch (err) {
      showErr(errorEl, err instanceof ApiError ? err.message : "Delete failed.");
    }
  });
}

// ── Watch Areas tab ────────────────────────────────────────────────────────
//
// The taxonomy pass 2 files every newly analysed signal into. Two things here
// differ from the Workstreams tab above, and both are deliberate:
//
//  - There is no Delete. A signal stores its area as a bare slug with no
//    foreign key, so deleting an area would leave its signals filed under a
//    name nothing resolves. Retire instead: the area stops being offered to the
//    model, and everything already filed under it stays filed.
//  - The slug is shown but never editable. It is what the stored signals point
//    at; renaming it would strand them exactly as deleting would.

function renderWatchAreasTab(container: HTMLElement): void {
  container.innerHTML = "";

  const intro = document.createElement("p");
  intro.className = "muted ap-wa-intro";
  intro.textContent =
    "Every signal analysed since routing shipped is filed into exactly one of " +
    "these. A definition is prompt text — the model reads it when deciding " +
    "where a signal belongs — so editing one changes how the next article is filed.";
  container.appendChild(intro);

  // The two piles that are not areas. They sit above the table because the
  // unassigned count is the reason to look at this screen at all: it is what
  // says the taxonomy has a hole in it.
  const piles = document.createElement("div");
  piles.className = "ap-wa-piles";
  const unassigned = _watchAreas.unassigned;
  const unrouted = _watchAreas.unrouted;
  piles.innerHTML = `
    <div class="ap-wa-pile">
      <span class="ap-wa-pile-num">${unassigned}</span>
      <span class="ap-wa-pile-label">unassigned</span>
      <span class="muted ap-wa-pile-hint">asked, and nothing fitted — the pile that says an area may be missing</span>
    </div>
    <div class="ap-wa-pile">
      <span class="ap-wa-pile-num">${unrouted}</span>
      <span class="ap-wa-pile-label">never routed</span>
      <span class="muted ap-wa-pile-hint">collected before routing existed, added by hand, or analysed offline</span>
    </div>
  `;
  const reviewBtn = document.createElement("button");
  reviewBtn.className = "primary ap-wa-review-btn";
  reviewBtn.textContent = "Review the unassigned pile";
  reviewBtn.disabled = unassigned === 0;
  reviewBtn.title =
    unassigned === 0
      ? "Nothing is unassigned — there is nothing to review."
      : "Ask the model what areas the pile suggests are missing.";
  reviewBtn.addEventListener("click", () => void runReview(reviewBtn));
  piles.querySelector(".ap-wa-pile")!.appendChild(reviewBtn);

  container.appendChild(piles);
  renderProposals(container);

  const toolbar = document.createElement("div");
  toolbar.className = "ap-toolbar";

  const filterInput = document.createElement("input");
  filterInput.type = "text";
  filterInput.className = "ap-input ap-filter-input";
  filterInput.placeholder = "Filter by name…";
  filterInput.value = _waFilter;
  filterInput.setAttribute("aria-label", "Filter watch areas");
  filterInput.addEventListener("input", () => {
    _waFilter = filterInput.value;
    renderWaRows(tbody);
  });

  const addBtn = document.createElement("button");
  addBtn.className = "primary";
  addBtn.textContent = "+ Add Watch Area";
  addBtn.addEventListener("click", () => openAddWatchAreaModal(refreshWatchAreasTab));

  toolbar.appendChild(filterInput);
  toolbar.appendChild(addBtn);
  container.appendChild(toolbar);

  const tableWrap = document.createElement("div");
  tableWrap.className = "ap-table-wrap";
  tableWrap.innerHTML = `
    <table class="ap-table ap-wa-table">
      <thead>
        <tr>
          <th>Area</th>
          <th>Slug</th>
          <th>Signals</th>
          <th>Status</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody id="ap-wa-tbody"></tbody>
    </table>
  `;
  container.appendChild(tableWrap);

  const tbody = tableWrap.querySelector<HTMLTableSectionElement>("#ap-wa-tbody")!;
  renderWaRows(tbody);
}

function renderWaRows(tbody: HTMLTableSectionElement): void {
  tbody.innerHTML = "";
  const q = _waFilter.toLowerCase();
  const all = _watchAreas.areas;
  const filtered = q
    ? all.filter(
        (a) => a.label.toLowerCase().includes(q) || a.slug.includes(q)
      )
    : all;

  if (filtered.length === 0) {
    const tr = document.createElement("tr");
    tr.innerHTML = `<td colspan="5" class="muted" style="padding:12px;text-align:center;">${
      all.length === 0 ? "No watch areas yet." : "No watch areas match the filter."
    }</td>`;
    tbody.appendChild(tr);
    return;
  }

  filtered.forEach((area) => {
    const tr = document.createElement("tr");
    if (!area.active) tr.className = "ap-wa-retired-row";
    tr.innerHTML = `
      <td>
        <div>${esc(area.label)}</div>
        <div class="muted ap-wa-definition">${esc(area.definition)}</div>
      </td>
      <td class="ap-td-mono">${esc(area.slug)}</td>
      <td class="ap-td-mono">${area.signalCount}</td>
      <td><span class="ap-role-badge ${
        area.active ? "ap-wa-active" : "ap-wa-retired"
      }">${area.active ? "Active" : "Retired"}</span></td>
      <td class="ap-td-actions"></td>
    `;
    const actionsCell = tr.querySelector<HTMLTableCellElement>(".ap-td-actions")!;

    const editBtn = document.createElement("button");
    editBtn.className = "ap-action-btn";
    editBtn.textContent = "Edit";
    editBtn.addEventListener("click", () => openEditWatchAreaModal(area, refreshWatchAreasTab));

    const toggleBtn = document.createElement("button");
    toggleBtn.className = "ap-action-btn";
    toggleBtn.textContent = area.active ? "Retire" : "Restore";
    toggleBtn.addEventListener("click", () =>
      openRetireWatchAreaModal(area, refreshWatchAreasTab)
    );

    actionsCell.appendChild(editBtn);
    actionsCell.appendChild(toggleBtn);
    tbody.appendChild(tr);
  });
}

async function refreshWatchAreasTab(): Promise<void> {
  const content = document.getElementById("ap-content");
  if (!content || _activeTab !== "watchareas") return;
  await Promise.all([loadWatchAreas(), loadProposals()]);
  renderWatchAreasTab(content);
}

// ── Add Watch Area Modal ───────────────────────────────────────────────────

function openAddWatchAreaModal(onDone: () => void): void {
  const overlay = createOverlay();
  overlay.innerHTML = `
    <div class="ap-modal ap-modal-wide" role="dialog" aria-modal="true" aria-label="Add Watch Area">
      <div class="ap-modal-header">
        <h2>Add Watch Area</h2>
        <button class="ap-modal-close" aria-label="Close">✕</button>
      </div>
      <div class="ap-modal-body">
        <label class="ap-field-label" for="wa-add-label">Name</label>
        <input type="text" id="wa-add-label" class="ap-input" autocomplete="off" />
        <p class="muted ap-def-hint" id="wa-add-slug">The slug is derived from the name and cannot be changed afterwards.</p>
        <label class="ap-field-label" for="wa-add-definition">Definition</label>
        <p class="muted ap-def-hint">What belongs here, in a sentence or two. The routing model reads this verbatim, so name concrete things rather than describing the area in the abstract.</p>
        <textarea
          id="wa-add-definition"
          class="ap-input ap-def-textarea"
          placeholder="e.g. Post-quantum cryptography migration, cryptographic standards and the policy timetable around them…"
          spellcheck="true"
        ></textarea>
        <p class="ap-error" id="wa-add-error" style="display:none;"></p>
      </div>
      <div class="ap-modal-footer">
        <button class="ap-modal-cancel">Cancel</button>
        <button class="primary" id="wa-add-submit">Create</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const labelInput = overlay.querySelector<HTMLInputElement>("#wa-add-label")!;
  const defInput = overlay.querySelector<HTMLTextAreaElement>("#wa-add-definition")!;
  const slugHint = overlay.querySelector<HTMLParagraphElement>("#wa-add-slug")!;
  labelInput.focus();

  // Previewed rather than left as a surprise: the slug is permanent, and it is
  // what every signal filed here will store.
  labelInput.addEventListener("input", () => {
    const slug = slugify(labelInput.value);
    slugHint.textContent = slug
      ? `Slug: ${slug} — derived from the name, and permanent.`
      : "The slug is derived from the name and cannot be changed afterwards.";
  });

  const close = () => overlay.remove();
  wireClose(overlay, close);

  overlay.querySelector("#wa-add-submit")!.addEventListener("click", async () => {
    const errorEl = overlay.querySelector("#wa-add-error") as HTMLParagraphElement;
    errorEl.style.display = "none";
    try {
      await api("/watch-areas", {
        method: "POST",
        body: { label: labelInput.value.trim(), definition: defInput.value.trim() },
      });
      close();
      onDone();
    } catch (err) {
      showErr(errorEl, err instanceof ApiError ? err.message : "Failed to create watch area.");
    }
  });
}

// ── Edit Watch Area Modal ──────────────────────────────────────────────────

function openEditWatchAreaModal(area: WatchAreaRecord, onDone: () => void): void {
  const overlay = createOverlay();
  overlay.innerHTML = `
    <div class="ap-modal ap-modal-wide" role="dialog" aria-modal="true" aria-label="Edit Watch Area">
      <div class="ap-modal-header">
        <h2>Edit — ${esc(area.label)}</h2>
        <button class="ap-modal-close" aria-label="Close">✕</button>
      </div>
      <div class="ap-modal-body">
        <label class="ap-field-label" for="wa-edit-label">Name</label>
        <input type="text" id="wa-edit-label" class="ap-input" autocomplete="off" />
        <p class="muted ap-def-hint">Slug: <code>${esc(area.slug)}</code> — fixed. ${
          area.signalCount
        } signal${area.signalCount === 1 ? "" : "s"} point at it.</p>
        <label class="ap-field-label" for="wa-edit-definition">Definition</label>
        <p class="muted ap-def-hint">Changing this changes how the next article is filed. Signals already filed here keep their filing.</p>
        <textarea id="wa-edit-definition" class="ap-input ap-def-textarea" spellcheck="true"></textarea>
        <p class="ap-error" id="wa-edit-error" style="display:none;"></p>
      </div>
      <div class="ap-modal-footer">
        <button class="ap-modal-cancel">Cancel</button>
        <button class="primary" id="wa-edit-submit">Save</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const labelInput = overlay.querySelector<HTMLInputElement>("#wa-edit-label")!;
  const defInput = overlay.querySelector<HTMLTextAreaElement>("#wa-edit-definition")!;
  labelInput.value = area.label;
  defInput.value = area.definition;
  labelInput.focus();

  const close = () => overlay.remove();
  wireClose(overlay, close);

  overlay.querySelector("#wa-edit-submit")!.addEventListener("click", async () => {
    const errorEl = overlay.querySelector("#wa-edit-error") as HTMLParagraphElement;
    errorEl.style.display = "none";
    try {
      await api(`/watch-areas/${encodeURIComponent(area.slug)}`, {
        method: "PATCH",
        body: { label: labelInput.value.trim(), definition: defInput.value.trim() },
      });
      close();
      onDone();
    } catch (err) {
      showErr(errorEl, err instanceof ApiError ? err.message : "Failed to save.");
    }
  });
}

// ── Retire / Restore Watch Area Modal ──────────────────────────────────────

function openRetireWatchAreaModal(area: WatchAreaRecord, onDone: () => void): void {
  const retiring = area.active;
  const overlay = createOverlay();
  const filed = `${area.signalCount} signal${area.signalCount === 1 ? "" : "s"}`;
  overlay.innerHTML = `
    <div class="ap-modal" role="dialog" aria-modal="true">
      <div class="ap-modal-header">
        <h2>${retiring ? "Retire" : "Restore"} Watch Area</h2>
        <button class="ap-modal-close" aria-label="Close">✕</button>
      </div>
      <div class="ap-modal-body">
        <p class="ap-warning-text">${
          retiring
            ? `Retire <strong>${esc(area.label)}</strong>? It stops being offered to the model, so nothing new is filed here. The ${filed} already filed under it keep their filing and stay countable — this is not a delete, and it can be undone.`
            : `Restore <strong>${esc(area.label)}</strong>? It goes back into the list the model chooses from, and new signals can be filed here again.`
        }</p>
        <p class="ap-error" id="wa-toggle-error" style="display:none;"></p>
      </div>
      <div class="ap-modal-footer">
        <button class="ap-modal-cancel">Cancel</button>
        <button class="${retiring ? "destructive" : "primary"}" id="wa-toggle-confirm">${
          retiring ? "Retire" : "Restore"
        }</button>
      </div>
    </div>
  `;
  document.body.appendChild(overlay);

  const close = () => overlay.remove();
  wireClose(overlay, close);

  overlay.querySelector("#wa-toggle-confirm")!.addEventListener("click", async () => {
    const errorEl = overlay.querySelector("#wa-toggle-error") as HTMLParagraphElement;
    errorEl.style.display = "none";
    try {
      await api(`/watch-areas/${encodeURIComponent(area.slug)}`, {
        method: "PATCH",
        body: { active: !area.active },
      });
      close();
      onDone();
    } catch (err) {
      showErr(errorEl, err instanceof ApiError ? err.message : "Failed to save.");
    }
  });
}

/**
 * The slug the backend will derive, previewed while typing.
 *
 * Mirrors `analyzer.normalise_wikilink`, which is what the API actually calls —
 * CamelCase split, separators to hyphens, lowercase, non-alphanumerics dropped,
 * runs of hyphens collapsed. It is a preview only: the server derives the real
 * one, so a drift here shows a wrong hint, never a wrong slug.
 */
function slugify(name: string): string {
  return name
    .replace(/([a-z0-9])([A-Z])/g, "$1-$2")
    .replace(/[\s_/\\]+/g, "-")
    .toLowerCase()
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "");
}

// ── Watch-area proposals ───────────────────────────────────────────────────
//
// The reviewer reads the unassigned pile and proposes changes; nothing here
// changes the taxonomy on its own. Two kinds only, `add` and `merge` — there is
// no retirement proposal, because an empty area is the coverage-gap signal the
// taxonomy exists to produce and a reviewer suggesting you stop looking at
// something would quietly delete the finding.

function renderProposals(container: HTMLElement): void {
  const pending = _proposals.filter((p) => p.status === "pending");
  const decided = _proposals.filter((p) => p.status !== "pending");
  if (pending.length === 0 && decided.length === 0 && !_reviewNotice) return;

  const wrap = document.createElement("div");
  wrap.className = "ap-wa-proposals";

  if (_reviewNotice) {
    const notice = document.createElement("p");
    notice.className = _reviewFailed ? "ap-error" : "muted ap-def-hint";
    notice.style.display = "block";
    notice.textContent = _reviewNotice;
    wrap.appendChild(notice);
  }

  if (pending.length > 0) {
    const h = document.createElement("h3");
    h.className = "ap-wa-proposals-title";
    h.textContent = `${pending.length} proposal${pending.length === 1 ? "" : "s"} awaiting a decision`;
    wrap.appendChild(h);
    pending.forEach((p) => wrap.appendChild(proposalCard(p)));
  }

  if (decided.length > 0) {
    // Kept visible rather than cleared away: a rejection is a decision
    // somebody made, and it is also what stops the reviewer proposing the same
    // thing next time. It should read as a record, not as a gap.
    const details = document.createElement("details");
    details.className = "ap-wa-decided";
    const summary = document.createElement("summary");
    summary.textContent = `${decided.length} already decided`;
    details.appendChild(summary);
    decided.forEach((p) => {
      const line = document.createElement("div");
      line.className = "muted ap-wa-decided-line";
      line.textContent =
        `${p.status === "accepted" ? "Accepted" : "Rejected"} — ` +
        (p.kind === "add"
          ? `add ${p.label ?? p.slug} (${p.slug})`
          : `merge ${p.slug} into ${p.mergeInto}`);
      details.appendChild(line);
    });
    wrap.appendChild(details);
  }

  container.appendChild(wrap);
}

function proposalCard(p: ProposalRecord): HTMLElement {
  const card = document.createElement("div");
  card.className = "ap-wa-proposal";

  const head = document.createElement("div");
  head.className = "ap-wa-proposal-head";
  head.innerHTML =
    p.kind === "add"
      ? `<span class="ap-role-badge ap-wa-kind-add">New area</span>
         <strong>${esc(p.label ?? p.slug)}</strong>
         <code class="ap-td-mono">${esc(p.slug)}</code>`
      : `<span class="ap-role-badge ap-wa-kind-merge">Merge</span>
         <strong>${esc(p.slug)}</strong> into <strong>${esc(p.mergeInto ?? "")}</strong>`;
  card.appendChild(head);

  const why = document.createElement("p");
  why.className = "ap-wa-proposal-why";
  why.textContent = p.rationale;
  card.appendChild(why);

  const def = document.createElement("p");
  def.className = "muted ap-wa-definition";
  def.textContent =
    (p.kind === "add" ? "Definition: " : `New definition for ${p.mergeInto}: `) + p.definition;
  card.appendChild(def);

  // The signals it clustered, by title. The point of showing them is that the
  // claim can be checked against the corpus rather than taken on trust.
  if (p.evidence.length > 0) {
    const ev = document.createElement("details");
    ev.className = "ap-wa-evidence";
    const s = document.createElement("summary");
    s.textContent = `${p.evidence.length} signal${p.evidence.length === 1 ? "" : "s"} behind this`;
    ev.appendChild(s);
    p.evidence.forEach((e) => {
      const line = document.createElement("div");
      line.className = "muted ap-wa-decided-line";
      line.textContent = e.title;
      ev.appendChild(line);
    });
    card.appendChild(ev);
  }

  const actions = document.createElement("div");
  actions.className = "ap-wa-proposal-actions";
  const accept = document.createElement("button");
  accept.className = "primary";
  accept.textContent = "Accept";
  accept.addEventListener("click", () => void decideProposal(p, "accept", card));
  const reject = document.createElement("button");
  reject.className = "ap-action-btn";
  reject.textContent = "Reject";
  reject.addEventListener("click", () => void decideProposal(p, "reject", card));
  actions.appendChild(accept);
  actions.appendChild(reject);
  card.appendChild(actions);

  const err = document.createElement("p");
  err.className = "ap-error";
  err.style.display = "none";
  err.id = `wa-prop-err-${p.id}`;
  card.appendChild(err);

  return card;
}

async function decideProposal(
  p: ProposalRecord,
  action: "accept" | "reject",
  card: HTMLElement
): Promise<void> {
  const err = card.querySelector<HTMLParagraphElement>(`#wa-prop-err-${p.id}`)!;
  err.style.display = "none";
  try {
    await api(`/watch-areas/proposals/${encodeURIComponent(p.id)}/${action}`, {
      method: "POST",
    });
    _reviewNotice = "";
    _reviewFailed = false;
    await refreshWatchAreasTab();
  } catch (e) {
    // An accept can fail on a re-check the reviewer passed but the taxonomy has
    // since invalidated. That message names what changed, so it belongs on the
    // card rather than in a generic alert.
    showErr(err, e instanceof ApiError ? e.message : "Could not apply that.");
  }
}

async function runReview(button: HTMLButtonElement): Promise<void> {
  button.disabled = true;
  const original = button.textContent;
  button.textContent = "Reading the pile…";
  _reviewNotice = "";
  _reviewFailed = false;
  try {
    const res = await api<ReviewResponse>("/watch-areas/review", {
      method: "POST",
      // Above the server's own 90s call budget plus its rate-limit wait, so
      // this fires only when the request is genuinely lost. Without it `fetch`
      // waits forever and the button never comes back.
      timeoutMs: 150000,
    });
    const parts: string[] = [];
    if (res.message) parts.push(res.message);
    else if (res.proposals.length === 0) parts.push("The reviewer proposed nothing.");
    // The cap is stated, never silent: "read 100 of 400" and "read everything"
    // are different answers and the screen must not conflate them.
    if (res.pileTotal > res.pileRead) {
      parts.push(`Read the ${res.pileRead} most recent of ${res.pileTotal} unassigned.`);
    }
    if (res.dropped.length > 0) {
      parts.push(
        `${res.dropped.length} suggestion${res.dropped.length === 1 ? "" : "s"} did not pass ` +
          `the checks: ${res.dropped.join("; ")}.`
      );
    }
    _reviewNotice = parts.join(" ");
  } catch (e) {
    _reviewFailed = true;
    _reviewNotice = e instanceof ApiError ? e.message : "The review could not run.";
  } finally {
    button.disabled = false;
    button.textContent = original;
  }
  await refreshWatchAreasTab();
}

// ── Analytics tab ─────────────────────────────────────────────────────────

interface AnalyticsSummary {
  totals: {
    signals_all_time: number;
    signals_this_week: number;
    wikilinks: number;
    workstreams_active: number;
    briefs_generated: number;
  };
  avg_wikilinks_per_signal: number;
  workstream_coverage_pct: number;
  channel_breakdown: { rss: number; telegram: number; manual: number };
  daily_signal_volume: { date: string; count: number }[];
  top_emerging_topics: { name: string; acceleration: number; total: number }[];
}

async function renderAnalyticsTab(container: HTMLElement): Promise<void> {
  container.innerHTML = `<p class="muted ap-analytics-loading">Loading analytics…</p>`;
  let data: AnalyticsSummary;
  try {
    data = await api<AnalyticsSummary>("/analytics/summary");
  } catch {
    container.innerHTML = `<p class="muted ap-analytics-loading">Failed to load analytics.</p>`;
    return;
  }

  container.innerHTML = "";

  const wrap = document.createElement("div");
  wrap.className = "an-wrap";

  // ── Stat cards ─────────────────────────────────────────────────────────
  const cards = document.createElement("div");
  cards.className = "an-cards";
  const cardDefs: [string, string | number][] = [
    ["Total Signals", data.totals.signals_all_time.toLocaleString()],
    ["This Week", data.totals.signals_this_week.toLocaleString()],
    ["Avg Wikilinks / Signal", data.avg_wikilinks_per_signal],
    ["Workstream Coverage", `${data.workstream_coverage_pct}%`],
  ];
  for (const [label, value] of cardDefs) {
    const card = document.createElement("div");
    card.className = "an-card";
    card.innerHTML = `
      <span class="an-card-value">${value}</span>
      <span class="an-card-label">${label}</span>
    `;
    cards.appendChild(card);
  }
  wrap.appendChild(cards);

  // ── Daily signal volume chart ──────────────────────────────────────────
  const chartSection = document.createElement("div");
  chartSection.className = "an-section";
  const chartTitle = document.createElement("h3");
  chartTitle.className = "an-section-title";
  chartTitle.textContent = "Signal Volume — Last 28 Days (SGT)";
  chartSection.appendChild(chartTitle);
  chartSection.appendChild(buildVolumeChart(data.daily_signal_volume));
  wrap.appendChild(chartSection);

  // ── Two-column row: channels + topics ─────────────────────────────────
  const twoCol = document.createElement("div");
  twoCol.className = "an-two-col";

  // Channel breakdown
  const chanSection = document.createElement("div");
  chanSection.className = "an-section";
  const chanTitle = document.createElement("h3");
  chanTitle.className = "an-section-title";
  chanTitle.textContent = "Signal Channels";
  chanSection.appendChild(chanTitle);
  chanSection.appendChild(buildChannelBreakdown(data.channel_breakdown));
  const chanNote = document.createElement("p");
  chanNote.className = "an-note";
  chanNote.textContent = "Approximate — inferred from collection timing.";
  chanSection.appendChild(chanNote);
  twoCol.appendChild(chanSection);

  // Top emerging topics
  const topicsSection = document.createElement("div");
  topicsSection.className = "an-section";
  const topicsTitle = document.createElement("h3");
  topicsTitle.className = "an-section-title";
  topicsTitle.textContent = "Emerging Topics (30d)";
  topicsSection.appendChild(topicsTitle);
  topicsSection.appendChild(buildTopicsTable(data.top_emerging_topics));
  twoCol.appendChild(topicsSection);

  wrap.appendChild(twoCol);

  // ── Quick stats row ────────────────────────────────────────────────────
  const quickStats = document.createElement("div");
  quickStats.className = "an-quick-stats";
  const qsDefs: [string, string | number][] = [
    ["Total Wikilinks", data.totals.wikilinks.toLocaleString()],
    ["Active Workstreams", data.totals.workstreams_active.toLocaleString()],
    ["Briefs Generated", data.totals.briefs_generated.toLocaleString()],
  ];
  for (const [label, value] of qsDefs) {
    const item = document.createElement("div");
    item.className = "an-qs-item";
    item.innerHTML = `<span class="an-qs-value">${value}</span><span class="an-qs-label">${label}</span>`;
    quickStats.appendChild(item);
  }
  wrap.appendChild(quickStats);

  container.appendChild(wrap);
}

function buildVolumeChart(series: { date: string; count: number }[]): SVGSVGElement {
  const VW = 600;
  const VH = 100;
  const CHART_H = 72;   // bar area height; bottom 28px reserved for date labels
  const GAP = 2;
  const n = series.length;
  const barW = Math.max(4, Math.floor((VW - GAP * (n - 1)) / n));
  const maxCount = Math.max(...series.map((s) => s.count), 1);

  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", `0 0 ${VW} ${VH}`);
  svg.setAttribute("width", "100%");
  svg.setAttribute("height", "auto");
  svg.style.display = "block";
  svg.classList.add("an-chart-svg");

  series.forEach((s, i) => {
    const barH = Math.max(2, Math.round((s.count / maxCount) * CHART_H));
    const x = i * (barW + GAP);
    const isToday = i === series.length - 1;

    const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
    rect.setAttribute("x", String(x));
    rect.setAttribute("y", String(CHART_H - barH));
    rect.setAttribute("width", String(barW));
    rect.setAttribute("height", String(barH));
    rect.setAttribute("rx", "1");
    rect.classList.add(isToday ? "an-bar-today" : "an-bar");
    const title = document.createElementNS("http://www.w3.org/2000/svg", "title");
    title.textContent = `${s.date}: ${s.count} signal${s.count !== 1 ? "s" : ""}`;
    rect.appendChild(title);
    svg.appendChild(rect);

    // Date label every 7 bars and on the last bar
    if (i % 7 === 0 || i === n - 1) {
      const label = document.createElementNS("http://www.w3.org/2000/svg", "text");
      label.setAttribute("x", String(x + barW / 2));
      label.setAttribute("y", String(VH - 4));
      label.setAttribute("text-anchor", "middle");
      label.classList.add("an-chart-label");
      label.textContent = s.date.slice(5); // MM-DD
      svg.appendChild(label);
    }
  });

  // Baseline
  const base = document.createElementNS("http://www.w3.org/2000/svg", "line");
  base.setAttribute("x1", "0");
  base.setAttribute("y1", String(CHART_H));
  base.setAttribute("x2", String(VW));
  base.setAttribute("y2", String(CHART_H));
  base.classList.add("an-chart-baseline");
  svg.appendChild(base);

  return svg;
}

function buildChannelBreakdown(ch: { rss: number; telegram: number; manual: number }): HTMLElement {
  const total = ch.rss + ch.telegram + ch.manual || 1;
  const rows: [string, number][] = [
    ["RSS Auto-collection", ch.rss],
    ["Telegram Bot", ch.telegram],
    ["Manual URL", ch.manual],
  ];
  const wrap = document.createElement("div");
  wrap.className = "an-channels";

  for (const [label, count] of rows) {
    const pct = Math.round((count / total) * 100);
    const row = document.createElement("div");
    row.className = "an-chan-row";
    row.innerHTML = `
      <span class="an-chan-label">${label}</span>
      <div class="an-chan-bar-wrap">
        <div class="an-chan-bar" style="width:${pct}%"></div>
      </div>
      <span class="an-chan-count">${count.toLocaleString()}</span>
      <span class="an-chan-pct">${pct}%</span>
    `;
    wrap.appendChild(row);
  }
  return wrap;
}

function buildTopicsTable(topics: { name: string; acceleration: number; total: number }[]): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "an-topics";

  if (topics.length === 0) {
    const empty = document.createElement("p");
    empty.className = "muted";
    empty.style.fontSize = "13px";
    empty.textContent = "No emerging topics yet. Collect more signals to see trends.";
    wrap.appendChild(empty);
    return wrap;
  }

  for (const t of topics) {
    const row = document.createElement("div");
    row.className = "an-topic-row";
    let badgeCls = "an-accel-flat";
    let badgeText = "→ stable";
    if (t.acceleration >= 1.5) {
      badgeCls = "an-accel-up";
      badgeText = `↑ ${t.acceleration.toFixed(1)}×`;
    } else if (t.acceleration < 0.8) {
      badgeCls = "an-accel-down";
      badgeText = `↓ ${t.acceleration.toFixed(1)}×`;
    }
    row.innerHTML = `
      <span class="an-topic-name">${esc(t.name)}</span>
      <span class="an-topic-badge ${badgeCls}">${badgeText}</span>
      <span class="an-topic-total">${t.total} signals</span>
    `;
    wrap.appendChild(row);
  }
  return wrap;
}

// ── Utility ────────────────────────────────────────────────────────────────

function esc(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

// fmtDate/fmtDateTime were local, and read UTC and browser-local respectively —
// two conventions inside one page. Both now come from lib/datetime and render in
// the app time zone, the same one the server buckets by.
